/**
 * Accounts Receivable domain — opportunity invoices + aging playbook.
 * Keep in sync with lib/accountsReceivable.js (email/API side).
 *
 * Invoice unit of truth: ALLGRANTS&SAVINGS opportunity fields
 * (AwardedAmount, SuccessFeeAmount, InvoiceSentDate, PaidInvoiceBy, Status).
 * Statement balanceDue is the client-facing A/R; CEM keeps waterfall as a check.
 */

import {
  formatTaxPercent,
  normalizeProvince,
  resolveInvoiceTax,
} from "./canadianTax.js";

export const DEFAULT_SUCCESS_FEE_RATE = 0.2;

/** Hourly rate for Cancelled opportunities (Hours / TimeRequired × rate). */
export const CANCELLED_HOURLY_RATE = 175;

export const DEFAULT_INVOICE_SELLER = {
  name: "Upgraded",
  legalName: "Upgraded Technologies Inc.",
  addressLines: ["Vancouver, British Columbia, Canada"],
  taxNumber: "",
  paymentNote:
    "Payment due within net terms. EFT or card details will be provided separately.",
};

export const INVOICE_PAYMENT_TERMS =
  "Payment terms: Net 15 from cash received, or Net 90 from awarded — whichever comes first. Interest on overdue accounts accrues at 12% per annum (1% per month), simple, calculated monthly, starting 30 days following the date payment is due. No interest is charged through the due date or during the 30-day grace after due. Upgraded may suspend performance of its Services in the event that the Company fails to pay one or more invoices.";

/** Short interest note for invoice UI / email (ties interest to the payments ledger). */
export const INVOICE_INTEREST_BRIEF =
  "Interest (1%/mo after a 30-day grace) applies only to fees still unpaid after terms. Payments below are applied oldest-due first — so a line shows interest only on what’s left after those credits.";

/** Stripe: no-interest principal link is valid this many days after issue. */
export const INVOICE_PAYMENT_LINK_EXPIRY_DAYS = 30;
export const INVOICE_PAYMENT_LINK_EXPIRY_NOTE =
  "The no-interest payment link works for 30 days from this statement. After that it deactivates — use the pay-in-full (incl. interest) link, or ask us for a fresh statement.";
export const INVOICE_NO_INTEREST_NUDGE =
  "Pay the principal within 30 days to skip accrued interest. After 30 days the no-interest link stops working.";

export const DEFAULT_SUCCESS_FEE_PERCENT = 20;
export const DEFAULT_TEAM_SHARE_OF_FEE = 0.3; // 30% of fee = 6% of award
export const DEFAULT_NET_TERMS_DAYS = 15;

/** Days after awarded date when fee is due if no client cash-received date. */
export const AWARDED_NET_DAYS = 90;
/** Days after client receives funder cash when fee is due. */
export const CASH_RECEIVED_NET_DAYS = 15;
/** Days after due date before interest begins accruing. */
export const INTEREST_GRACE_DAYS = 30;
/** Simple monthly rate (12% p.a.). */
export const MONTHLY_INTEREST_RATE = 0.01;
/**
 * Opportunity field: date the client received cash from the funder.
 * Staff add this in Airtable; when empty, due falls back to awarded + 90d.
 */
export const CLIENT_CASH_RECEIVED_FIELD = "ClientCashReceivedDate";
/** Public financing partners portal — distinct from the pay-invoice CTA. */
export const FINANCING_PARTNERS_URL =
  typeof window !== 'undefined'
    ? `${window.location.origin}/financing-partners`
    : 'https://app.getupgraded.ca/financing-partners';
/** Public client guide for account statements and success-fee math. */
export const ACCOUNT_STATEMENT_GUIDE_PATH =
  "/help/how-success-fees-are-calculated";
export const ACCOUNT_STATEMENT_GUIDE_URL =
  typeof window !== "undefined"
    ? `${window.location.origin}${ACCOUNT_STATEMENT_GUIDE_PATH}`
    : `https://app.getupgraded.ca${ACCOUNT_STATEMENT_GUIDE_PATH}`;

export const normalizeAccountStatementWebsite = (website) =>
  String(website || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/.*$/, "");

/** Path or absolute URL for the public guide, scoped with ?website=&company= when known. */
export const buildAccountStatementGuideHref = ({
  website,
  companyName,
  origin,
} = {}) => {
  const params = new URLSearchParams();
  const domain = normalizeAccountStatementWebsite(website);
  const name = String(companyName || "").trim();
  if (domain) params.set("website", domain);
  if (name && name !== "—") params.set("company", name);
  const qs = params.toString();
  const path = qs
    ? `${ACCOUNT_STATEMENT_GUIDE_PATH}?${qs}`
    : ACCOUNT_STATEMENT_GUIDE_PATH;
  if (!origin) return path;
  return `${String(origin).replace(/\/$/, "")}${path}`;
};

export const buildAccountStatementGuideUrl = (options = {}) =>
  buildAccountStatementGuideHref({
    ...options,
    origin:
      options.origin ||
      (typeof window !== "undefined"
        ? window.location.origin
        : "https://app.getupgraded.ca"),
  });
/** Optional company JSON field for missed retainer months on the statement. */
export const MISSED_RETAINER_MONTHS_FIELD = "MissedRetainerMonths";
/** Opportunity JSON: payment plan schedule + installment status. */
export const PAYMENT_PLAN_FIELD = "PaymentPlan";
/** Opportunity JSON: { date, setBy, setAt, note } — overrides due date / interest clock. */
export const AR_EFFECTIVE_START_FIELD = "ArEffectiveStart";
/** Company date: last monthly automated statement send (text YYYY-MM-DD). */
export const LAST_MONTHLY_INVOICE_FIELD = "LastMonthlyInvoiceAt";
export const PAYMENT_PLAN_MONTH_OPTIONS = [1, 4, 6, 9, 12, 18, 24, 36, 48, 60];
/** 1 = Immediate (single installment for the full fee). */
export const DEFAULT_PAYMENT_PLAN_MONTHS = 1;
/** Company text field: opt into monthly automated statements ("true" / "false"). */
export const MONTHLY_INVOICE_OPT_IN_FIELD = "MonthlyArInvoiceOptIn";
/** Company text field: opt into weekly Slack-only internal QA preview statements. */
export const WEEKLY_AR_QA_OPT_IN_FIELD = "WeeklyArQaPreviewOptIn";
/** Company text stamp YYYY-MM-DD when weekly internal QA last ran for this company. */
export const LAST_WEEKLY_AR_QA_FIELD = "LastWeeklyArQaPreviewAt";
/** Company text field: opt into weekly client+team escalate when balance owing. */
export const WEEKLY_AR_ESCALATE_OPT_IN_FIELD = "WeeklyArEscalateOptIn";
/** Company text stamp YYYY-MM-DD when weekly escalate last ran for this company. */
export const LAST_WEEKLY_AR_ESCALATE_FIELD = "LastWeeklyArEscalateAt";
/** Monthly statements switch to weekly after this many overdue days. */
export const AR_WEEKLY_ESCALATION_AFTER_DAYS = 30;
/** Hard cap for all automated client-facing statements in a calendar month. */
export const AR_MAX_CLIENT_STATEMENTS_PER_MONTH = 4;
/** ClientTypes included in bulk Auto Invoice / weekly opt-in actions. */
export const AR_BULK_OPT_IN_CLIENT_TYPES = ["MA", "MP"];

/** Parse companies.MonthlyArInvoiceOptIn when stored as single-line text. */
export const isMonthlyInvoiceOptIn = (raw) => {
  if (raw === true || raw === 1) return true;
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  return s === "true" || s === "yes" || s === "1" || s === "on" || s === "y";
};

export const formatMonthlyInvoiceOptIn = (enabled) =>
  enabled ? "true" : "false";

/** Same truthy parsing as monthly opt-in (text field). */
export const isWeeklyArQaOptIn = isMonthlyInvoiceOptIn;
export const formatWeeklyArQaOptIn = formatMonthlyInvoiceOptIn;
export const isWeeklyArEscalateOptIn = isMonthlyInvoiceOptIn;
export const formatWeeklyArEscalateOptIn = formatMonthlyInvoiceOptIn;

/**
 * True only when the opt-in field was saved as off.
 * Blank stays eligible for a bulk on. An explicit off is left alone.
 */
export const isExplicitArOptOut = (raw) => {
  if (raw == null) return false;
  if (typeof raw === "boolean") return raw === false;
  if (String(raw).trim() === "") return false;
  return !isMonthlyInvoiceOptIn(raw);
};

const roundArMoney = (value) =>
  Math.round((Number(value) || 0) * 100) / 100;

/**
 * Amounts for the two Stripe links, matched to the figures on the statement.
 * No future installments: "incl. interest" is Balance remaining.
 * A payment plan: "incl. interest" is Due today (interest is collected now).
 * The no-interest link is that same payable amount minus accrued interest.
 */
export const resolveArPayLinkAmounts = (invoiceDoc = {}) => {
  const interest = Math.max(0, roundArMoney(invoiceDoc.interestAccrued));
  const balanceDue =
    invoiceDoc.balanceDue != null && invoiceDoc.balanceDue !== ""
      ? Math.max(0, roundArMoney(invoiceDoc.balanceDue))
      : null;
  const dueToday =
    invoiceDoc.dueToday != null && invoiceDoc.dueToday !== ""
      ? Math.max(0, roundArMoney(invoiceDoc.dueToday))
      : null;
  const dueLater = Math.max(0, roundArMoney(invoiceDoc.dueLater));
  const hasFutureDue =
    Boolean(invoiceDoc.showDueToday) && dueLater > 0.009;
  const fallback = Math.max(0, roundArMoney(invoiceDoc.totalDue));
  const withInterest = hasFutureDue
    ? dueToday ?? balanceDue ?? fallback
    : balanceDue ?? dueToday ?? fallback;
  return {
    withInterest: Math.max(0, roundArMoney(withInterest)),
    withoutInterest: Math.max(
      0,
      roundArMoney(withInterest - interest)
    ),
    interest,
    hasFutureDue,
  };
};

/** Pause between bulk weekly QA / escalate sends (Resend / Slack). Keep low enough for Vercel maxDuration. */
export const WEEKLY_AR_QA_SEND_GAP_MS = 2000;
export const WEEKLY_AR_ESCALATE_SEND_GAP_MS = WEEKLY_AR_QA_SEND_GAP_MS;

export const isArBulkOptInClientType = (clientType) =>
  AR_BULK_OPT_IN_CLIENT_TYPES.includes(String(clientType || "").trim().toUpperCase());

/** Monday-based ISO week key: YYYY-Www */
export const isoWeekKey = (isoOrDate) => {
  if (!isoOrDate) return null;
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return null;
  const utc = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  );
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((utc - yearStart) / 86400000 + 1) / 7);
  return `${utc.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
};

export const sameIsoWeek = (isoOrDate, asOf = new Date()) => {
  const a = isoWeekKey(isoOrDate);
  const b = isoWeekKey(asOf);
  return Boolean(a && b && a === b);
};

/** True when LastWeeklyArQaPreviewAt is empty or not this ISO week. */
export const needsWeeklyArQaPreview = (lastAt, asOf = new Date()) => {
  if (lastAt == null || String(lastAt).trim() === "") return true;
  return !sameIsoWeek(lastAt, asOf);
};

const sameUtcMonth = (isoOrDate, asOf = new Date()) => {
  if (!isoOrDate) return false;
  const d = new Date(isoOrDate);
  const current = asOf instanceof Date ? asOf : new Date(asOf);
  if (Number.isNaN(d.getTime()) || Number.isNaN(current.getTime())) return false;
  return (
    d.getUTCFullYear() === current.getUTCFullYear() &&
    d.getUTCMonth() === current.getUTCMonth()
  );
};

const utcDateOnly = (value) => {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  );
};

/**
 * True when a company should leave monthly cadence and receive weekly statements.
 * "Longer than 30 days" intentionally starts at day 31.
 */
export const requiresWeeklyArEscalation = (invoices = []) =>
  (invoices || []).some(
    (invoice) =>
      Number.isFinite(Number(invoice?.daysOverdue)) &&
      Number(invoice.daysOverdue) > AR_WEEKLY_ESCALATION_AFTER_DAYS
  );

/** Stage 3–5 ids. Daily reminders at every stage send only when WeeklyArEscalateOptIn is on. */
export const AR_CLIENT_ESCALATION_STAGE_IDS = [
  "escalation",
  "final",
  "collections",
];

export const isArClientEscalationStage = (stageId) =>
  AR_CLIENT_ESCALATION_STAGE_IDS.includes(String(stageId || ""));

/**
 * Weekly client-statement gate.
 * - never sends twice in an ISO week across monthly + weekly jobs
 * - reserves at most four calendar-month send slots across both jobs
 *
 * Weekly slots are counted from the later of month start or escalation start,
 * so a company that first crosses day 30 late in a month still receives its
 * first weekly statement. A monthly send consumes one of the four slots.
 */
export const needsWeeklyArEscalate = (
  lastWeeklyAt,
  asOf = new Date(),
  { lastMonthlyAt = null, escalationStartedAt = null } = {}
) => {
  if (sameIsoWeek(lastWeeklyAt, asOf) || sameIsoWeek(lastMonthlyAt, asOf)) {
    return false;
  }
  const current = asOf instanceof Date ? asOf : new Date(asOf);
  if (Number.isNaN(current.getTime())) return false;
  const monthlySlotsUsed = sameUtcMonth(lastMonthlyAt, current) ? 1 : 0;
  const availableWeeklySlots = Math.max(
    0,
    AR_MAX_CLIENT_STATEMENTS_PER_MONTH - monthlySlotsUsed
  );
  if (availableWeeklySlots === 0) return false;

  const currentDate = utcDateOnly(current);
  const monthStart = new Date(
    Date.UTC(current.getUTCFullYear(), current.getUTCMonth(), 1)
  );
  const escalationStart = utcDateOnly(escalationStartedAt);
  const slotWindowStart =
    escalationStart && escalationStart > monthStart
      ? escalationStart
      : monthStart;
  const firstMonday = new Date(slotWindowStart);
  firstMonday.setUTCDate(
    firstMonday.getUTCDate() + ((8 - firstMonday.getUTCDay()) % 7)
  );
  if (currentDate < firstMonday) return false;

  // Always allow the first eligible weekly send, including a late-month
  // escalation. Once weekly cadence has started this month, use fixed
  // calendar-Monday slots so a changing oldest invoice cannot reset the cap.
  if (!sameUtcMonth(lastWeeklyAt, current)) return true;
  const firstMondayOfMonth = new Date(monthStart);
  firstMondayOfMonth.setUTCDate(
    firstMondayOfMonth.getUTCDate() +
      ((8 - firstMondayOfMonth.getUTCDay()) % 7)
  );
  const calendarWeeklySlot =
    Math.floor(
      (currentDate - firstMondayOfMonth) / (7 * 24 * 60 * 60 * 1000)
    ) + 1;
  return calendarWeeklySlot <= availableWeeklySlots;
};

/** True when LastMonthlyInvoiceAt is empty (never sent a monthly statement). */
export const isNeverSentMonthlyInvoice = (lastAt) =>
  lastAt == null || String(lastAt).trim() === "";

/** Always CC'd on outbound A/R invoice emails. */
export const AR_INVOICE_ACCOUNTING_CC = "accounting@getupgraded.ca";
/** Resend From for A/R (verified getupgraded.ca domain). */
export const AR_INVOICE_FROM_EMAIL = "accounting@getupgraded.ca";
export const AR_INVOICE_FROM_NAME = "Upgraded Accounting";
export const arInvoiceFromAddress = (
  email = AR_INVOICE_FROM_EMAIL,
  name = AR_INVOICE_FROM_NAME
) => `${name} <${email}>`;
export const AR_INVOICE_FINANCE_BCC =
  "finance-accounting-aaaanygpp2uego45jjjxphfqtq@upgraded-hq.slack.com";

/**
 * Build Bill To lines from company Airtable fields (dynamic — not hardcoded).
 * Prefers structured City / ProvinceOrState / PostalCode; skips Address when it
 * is only a bare province name that would conflict with ProvinceOrState.
 */
export const buildBillToLines = (company = {}, billToName = "—") => {
  const provinceRaw =
    company.ProvinceOrState || company.Province || company.State || null;
  const streetRaw = company.Address || company.BillingAddress || null;
  const streetIsBareProvince =
    Boolean(streetRaw) &&
    Boolean(normalizeProvince(streetRaw)) &&
    !/[,\d]/.test(String(streetRaw));
  const province =
    provinceRaw || (streetIsBareProvince ? streetRaw : null) || null;
  const street = streetIsBareProvince ? null : streetRaw;
  const locationLine =
    [company.City, province, company.PostalCode].filter(Boolean).join(", ") ||
    null;
  return [billToName, street, locationLine].filter(Boolean);
};

/** Days to wait before the first auto-reminder after Send Invoice. */
export const INVOICE_FIRST_REMINDER_DAYS = 15;

/** Reminder cadence (days) by A/R stage once reminding. */
export const INVOICE_REMINDER_INTERVAL_DAYS = {
  current: 15,
  gentle: 7,
  firm: 7,
  escalation: 5,
  final: 5,
  collections: 14,
};

export const formatAirtableDate = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

export const addDaysToDate = (date, days) => {
  const d = date instanceof Date ? new Date(date) : new Date(date);
  d.setDate(d.getDate() + Number(days || 0));
  return d;
};

export const COMPLETED_COLLECTION_STATUSES = ["Awarded", "Cash Collected"];

export const isCompletedCollectionStatus = (status) =>
  COMPLETED_COLLECTION_STATUSES.includes(status);

export const isCancelledArStatus = (status) =>
  String(status || "")
    .trim()
    .toLowerCase() === "cancelled";

/** Statuses that contribute success / cancellation fees to Total Owed. */
export const isFeeOwedStatus = (status) =>
  isCompletedCollectionStatus(status) || isCancelledArStatus(status);

/** Escalation stages from overdue days (after invoice due date). */
export const AR_STAGES = [
  {
    id: "current",
    label: "Current",
    minDays: -Infinity,
    maxDays: 0,
    tone: "neutral",
    badgeClass: "bg-gray-100 text-gray-700",
    description: "Within net terms — not yet overdue",
  },
  {
    id: "gentle",
    label: "Stage 1 · Gentle",
    minDays: 1,
    maxDays: 15,
    tone: "warning",
    badgeClass: "bg-amber-50 text-amber-700",
    description: "1–15 days late — friendly nudge",
  },
  {
    id: "firm",
    label: "Stage 2 · Firm",
    minDays: 16,
    maxDays: 45,
    tone: "warning",
    badgeClass: "bg-amber-50 text-amber-800",
    description: "16–45 days late — confirm payment date",
  },
  {
    id: "escalation",
    label: "Stage 3 · Call",
    minDays: 46,
    maxDays: 75,
    tone: "error",
    badgeClass: "bg-red-50 text-red-600",
    description: "45–75 days — call + payment plan offer",
  },
  {
    id: "final",
    label: "Stage 4 · Final",
    minDays: 76,
    maxDays: 119,
    tone: "error",
    badgeClass: "bg-red-50 text-red-700",
    description: "75+ days — formal notice",
  },
  {
    id: "collections",
    label: "Stage 5 · Collections",
    minDays: 120,
    maxDays: Infinity,
    tone: "error",
    badgeClass: "bg-gray-900 text-white",
    description: "120+ days — collections / legal path",
  },
];

/** Canonical YYYY-MM-DD for sorting/display — handles ISO, Stripe, M/D/Y, Date objects. */
export const normalizePaidAtISO = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  const str = String(value).trim();
  let match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (match) {
    const y = Number(match[1]);
    const mo = Number(match[2]);
    const d = Number(match[3]);
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    }
  }
  match = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (match) {
    const mo = Number(match[1]);
    const d = Number(match[2]);
    const y = Number(match[3]);
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
      return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    }
  }
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return null;
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseDateOnly = (value) => {
  const iso = normalizePaidAtISO(value);
  if (!iso) return null;
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );
};

const daysBetween = (from, to) => {
  if (!from || !to) return null;
  const ms = to.getTime() - from.getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
};

export const formatArCurrency = (amount) => {
  const n = Number(amount) || 0;
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(n);
};

/**
 * Default subject for statement emails — client company, total awarded, seller.
 * Cancelled / hourly (non-award) lines are excluded from the awarded total.
 * e.g. "Account Statement for Acme Corp — $120,000 Awarded · Upgraded"
 */
export const buildInvoiceEmailSubject = (doc = {}) => {
  const companyName = doc.billTo?.name || doc.companyName || "your company";
  const awarded =
    doc.totalAwarded != null
      ? Number(doc.totalAwarded)
      : (doc.lineItems || []).reduce((sum, item) => {
          if (item?.hoursBased || item?.feeHoursBased) return sum;
          return (
            sum + (Number(item.awarded) || Number(item.recovered) || 0)
          );
        }, 0);
  const sellerName =
    doc.seller?.name || DEFAULT_INVOICE_SELLER.name || "Upgraded";
  if (Number.isFinite(awarded) && awarded > 0) {
    return `Account Statement for ${companyName} — ${formatArCurrency(awarded)} Awarded · ${sellerName}`;
  }
  return `Account Statement for ${companyName} · ${sellerName}`;
};

export const getOpportunityFields = (opportunity) => {
  if (!opportunity) return {};

  // Merge so sparse local overrides (e.g. SuccessFeePercent-only `fields`)
  // never wipe Status / TimeRequired from flattened board rows or Airtable records.
  const fromOriginal = opportunity.originalRecord?.fields || {};
  const fromNested = opportunity.fields || {};
  const flat = {};
  for (const [key, value] of Object.entries(opportunity)) {
    if (
      key === "fields" ||
      key === "originalRecord" ||
      key === "opportunity" ||
      key === "id" ||
      key === "ID"
    ) {
      continue;
    }
    if (value !== undefined) flat[key] = value;
  }

  if (
    Object.keys(fromOriginal).length === 0 &&
    Object.keys(fromNested).length === 0
  ) {
    return Object.keys(flat).length ? flat : opportunity;
  }

  return {
    ...fromOriginal,
    ...flat,
    ...fromNested,
  };
};

export const getRecoveredAmount = (opportunity) => {
  const fields = getOpportunityFields(opportunity);
  const awarded = Number(fields.AwardedAmount);
  if (Number.isFinite(awarded) && awarded > 0) return awarded;
  const adjusted = Number(fields.AdjustedAmount);
  if (Number.isFinite(adjusted) && adjusted > 0) return adjusted;
  const amount = Number(fields.Amount);
  if (Number.isFinite(amount) && amount > 0) return amount;
  return 0;
};

/** Funding from Awarded + Cash Collected opportunities (completed section). */
export const getCompletedFundingTotal = (opportunities = []) =>
  (opportunities || []).reduce((sum, opportunity) => {
    const status = getOpportunityFields(opportunity).Status || "";
    if (!isCompletedCollectionStatus(status)) return sum;
    return sum + getRecoveredAmount(opportunity);
  }, 0);

export const resolveSuccessFeePercent = (
  value,
  fallback = DEFAULT_SUCCESS_FEE_PERCENT
) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return n > 1 ? n : n * 100;
};

const companyIdKey = (companyId) => {
  if (Array.isArray(companyId)) return companyId[0] || null;
  return companyId || null;
};

export const findCompanyForOpportunity = (opportunity, companies = []) => {
  if (!Array.isArray(companies) || companies.length === 0) return null;
  const id = companyIdKey(getOpportunityFields(opportunity).CompanyID);
  if (!id) return null;
  return companies.find((c) => c.id === id) || null;
};

/**
 * Resolve success fee $ for one opportunity.
 * Prefer SuccessFeeAmount (legacy $ override); else recovered × feeRate.
 */
export const resolveSuccessFeeAmount = (
  opportunity,
  feeRate = DEFAULT_SUCCESS_FEE_RATE
) => {
  const fields = getOpportunityFields(opportunity);
  const dollarOverride = Number(fields.SuccessFeeAmount);
  if (
    Number.isFinite(dollarOverride) &&
    dollarOverride >= 0 &&
    String(fields.SuccessFeeAmount).trim() !== ""
  ) {
    return dollarOverride;
  }
  const rate =
    Number(feeRate) > 1
      ? Number(feeRate) / 100
      : Number(feeRate) || DEFAULT_SUCCESS_FEE_RATE;
  return getRecoveredAmount(opportunity) * rate;
};

/**
 * Billable hours for Cancelled opportunities.
 * Prefer TimeRequired (AR table field); accept Hours if present on other views.
 */
export const getBillableHours = (opportunity) => {
  const fields = getOpportunityFields(opportunity);
  const timeRequired = Number(fields.TimeRequired);
  if (Number.isFinite(timeRequired) && timeRequired > 0) return timeRequired;
  const hours = Number(fields.Hours);
  if (Number.isFinite(hours) && hours > 0) return hours;
  return 0;
};

export const resolveCancelledFeeAmount = (opportunity) => {
  const fields = getOpportunityFields(opportunity);
  const dollarOverride = Number(fields.SuccessFeeAmount);
  if (
    Number.isFinite(dollarOverride) &&
    dollarOverride >= 0 &&
    String(fields.SuccessFeeAmount).trim() !== ""
  ) {
    return dollarOverride;
  }
  return getBillableHours(opportunity) * CANCELLED_HOURLY_RATE;
};

/**
 * Fee owed for A/R — Cancelled = Hours × $175/hr; otherwise success fee %.
 */
export const resolveFeeOwedAmount = (
  opportunity,
  feeRate = DEFAULT_SUCCESS_FEE_RATE
) => {
  const status = getOpportunityFields(opportunity).Status || "";
  if (isCancelledArStatus(status)) {
    return resolveCancelledFeeAmount(opportunity);
  }
  return resolveSuccessFeeAmount(opportunity, feeRate);
};

export const getOpportunityFeePercentOverride = (opportunity) => {
  const fields = getOpportunityFields(opportunity);
  const raw = fields.SuccessFeePercent;
  if (raw === null || raw === undefined || String(raw).trim() === "") {
    return null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  // Accept 0.2 or 20 as percent
  return n > 0 && n <= 1 ? n * 100 : n;
};

export const getFeeRateForOpportunity = (
  opportunity,
  {
    feeRate = DEFAULT_SUCCESS_FEE_RATE,
    successFeePercent = null,
    companies = null,
  } = {}
) => {
  const oppPercent = getOpportunityFeePercentOverride(opportunity);
  if (oppPercent != null) {
    return oppPercent / 100;
  }
  if (successFeePercent != null) {
    return resolveSuccessFeePercent(successFeePercent) / 100;
  }
  const company = findCompanyForOpportunity(opportunity, companies);
  const fromCompany = company?.fields?.SuccessFeePercent;
  if (fromCompany != null && fromCompany !== "") {
    return resolveSuccessFeePercent(fromCompany) / 100;
  }
  return Number(feeRate) > 1
    ? Number(feeRate) / 100
    : Number(feeRate) || DEFAULT_SUCCESS_FEE_RATE;
};

/**
 * Total success fees owed on completed awards (honours per-opp SuccessFeeAmount).
 */
export const getTotalOwedSuccessFees = (
  opportunities = [],
  feePercent = DEFAULT_SUCCESS_FEE_PERCENT,
  companies = null
) => {
  const defaultRate =
    Number(feePercent) > 1
      ? Number(feePercent) / 100
      : Number(feePercent) || DEFAULT_SUCCESS_FEE_RATE;
  return (opportunities || []).reduce((sum, opportunity) => {
    const status = getOpportunityFields(opportunity).Status || "";
    if (!isFeeOwedStatus(status)) return sum;
    const rate = getFeeRateForOpportunity(opportunity, {
      feeRate: defaultRate,
      companies,
    });
    return sum + resolveFeeOwedAmount(opportunity, rate);
  }, 0);
};

/**
 * Company ledger comparison: Paid to Date vs Total Owed (completed × fee%).
 */
export const buildPaidVsOwedSummary = (
  opportunities = [],
  {
    paidToDate = null,
    successFeePaidToDate = null,
    successFeePercent = DEFAULT_SUCCESS_FEE_PERCENT,
    companies = null,
  } = {}
) => {
  let feePercent = resolveSuccessFeePercent(successFeePercent);
  let paid =
    successFeePaidToDate != null
      ? Number(successFeePaidToDate) || 0
      : paidToDate == null
        ? null
        : Number(paidToDate) || 0;
  let taxSampleFields = null;

  // Portfolio mode: aggregate PaidToDate / fee% from company records when provided
  if (Array.isArray(companies) && companies.length > 0) {
    const companyIds = new Set(
      (opportunities || [])
        .map((o) => getOpportunityFields(o).CompanyID)
        .filter(Boolean)
        .flatMap((id) => (Array.isArray(id) ? id : [id]))
    );
    const relevant = companies.filter((c) => companyIds.has(c.id));
    const source = relevant.length > 0 ? relevant : companies;
    paid = source.reduce(
      (sum, c) => {
        const feePaid = c.fields?.SuccessFeePaidToDate;
        return (
          sum +
          (Number(
            feePaid != null && feePaid !== ""
              ? feePaid
              : c.fields?.PaidToDate
          ) || 0)
        );
      },
      0
    );
    const feeValues = source
      .map((c) => Number(c.fields?.SuccessFeePercent))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (feeValues.length > 0) {
      feePercent =
        feeValues.reduce((a, b) => a + b, 0) / feeValues.length;
    }
    taxSampleFields = source[0]?.fields || null;
  }

  const completedFunding = getCompletedFundingTotal(opportunities);
  const totalOwed = getTotalOwedSuccessFees(
    opportunities,
    feePercent,
    companies
  );
  const paidSafe = paid == null ? null : Math.max(0, paid);
  const tax = taxSampleFields
    ? resolveInvoiceTax(totalOwed, taxSampleFields)
    : null;
  const taxTotal = tax?.taxTotal ?? null;
  const totalWithTax = tax?.totalWithTax ?? totalOwed;

  // PaidToDate is tax-inclusive — Remaining compares apples-to-apples vs Total Owed + tax
  const remaining =
    paidSafe == null ? null : Math.max(0, totalWithTax - paidSafe);

  const paidTaxPortion =
    paidSafe == null || !(totalWithTax > 0) || taxTotal == null
      ? null
      : Math.round(((paidSafe * taxTotal) / totalWithTax) * 100) / 100;
  const paidExTax =
    paidSafe == null || paidTaxPortion == null
      ? null
      : Math.round((paidSafe - paidTaxPortion) * 100) / 100;
  const remainingTax =
    remaining == null || !(totalWithTax > 0) || taxTotal == null
      ? null
      : Math.round(((remaining * taxTotal) / totalWithTax) * 100) / 100;

  return {
    completedFunding,
    totalOwed,
    paidToDate: paidSafe,
    remaining,
    successFeePercent: feePercent,
    tax,
    taxTotal,
    totalWithTax,
    paidTaxPortion,
    paidExTax,
    remainingTax,
    paidIncludesTax: true,
    completedCount: (opportunities || []).filter((o) =>
      isFeeOwedStatus(getOpportunityFields(o).Status || "")
    ).length,
  };
};

export const getSuccessFeeOwed = (
  opportunity,
  feeRate = DEFAULT_SUCCESS_FEE_RATE
) => resolveFeeOwedAmount(opportunity, feeRate);

export const getTeamPayout = (
  opportunity,
  feeRate = DEFAULT_SUCCESS_FEE_RATE,
  teamShare = DEFAULT_TEAM_SHARE_OF_FEE
) => getSuccessFeeOwed(opportunity, feeRate) * teamShare;

export const isInvoicePaid = (opportunity) => {
  const fields = getOpportunityFields(opportunity);
  const paidBy = String(fields.PaidInvoiceBy || "").trim();
  return Boolean(paidBy);
};

export const isInvoiceOpen = (opportunity, options = {}) => {
  const fields = getOpportunityFields(opportunity);
  const status = fields.Status || "";

  // Cash Collected means the fee has been collected — drop from A/R list
  if (status === "Cash Collected") return false;
  if (isInvoicePaid(opportunity)) return false;

  // Cancelled: always include on Collect / Invoice (Hours × $175/hr).
  // Rows without hours still appear so the team can set TimeRequired.
  if (isCancelledArStatus(status)) return true;

  const rate = getFeeRateForOpportunity(opportunity, options);
  const feeOwed = resolveFeeOwedAmount(opportunity, rate);
  if (!(feeOwed > 0)) return false;

  const invoiceSent = Boolean(fields.InvoiceSentDate);
  const awardedLike = status === "Awarded" || invoiceSent;
  return awardedLike;
};

/**
 * Success-fee due date = min(cash_received + 15d, awarded + 90d).
 * Cash received: opportunity ClientCashReceivedDate, else company-level same field.
 * Explicit ArEffectiveStart (logged backdate) overrides when set.
 * Cancelled / no-award fallback: InvoiceSentDate + netTermsDays (or null).
 */
export const getInvoiceDueDate = (
  opportunity,
  netTermsDays = DEFAULT_NET_TERMS_DAYS,
  companyFields = null
) => {
  const fields = getOpportunityFields(opportunity);
  const company =
    companyFields ||
    opportunity?.companyFields ||
    opportunity?.fields?.__companyFields ||
    null;
  const effective = parseArEffectiveStart(
    fields[AR_EFFECTIVE_START_FIELD] || fields.ArEffectiveStartDate
  );
  if (effective?.date) {
    return parseDateOnly(effective.date);
  }

  const awardedDate = parseDateOnly(fields.EstHearBackDate);
  const cashReceived = parseDateOnly(
    fields[CLIENT_CASH_RECEIVED_FIELD] ||
      fields.CashReceivedDate ||
      company?.[CLIENT_CASH_RECEIVED_FIELD] ||
      company?.CashReceivedDate
  );

  const candidates = [];
  if (cashReceived) {
    candidates.push(addDaysToDate(cashReceived, CASH_RECEIVED_NET_DAYS));
  }
  if (awardedDate) {
    candidates.push(addDaysToDate(awardedDate, AWARDED_NET_DAYS));
  }
  if (candidates.length > 0) {
    return new Date(Math.min(...candidates.map((d) => d.getTime())));
  }

  // Cancelled hourly / missing award date — fall back to invoice sent + net terms
  const sent = parseDateOnly(fields.InvoiceSentDate);
  if (sent) {
    return addDaysToDate(sent, netTermsDays);
  }
  return null;
};

/**
 * Simple interest at 1%/month on principal, starting 30 days after due.
 * Through due + 29 days → $0. At due + 30 → first month (1%), then +1% each
 * full 30-day period. Never returns projected/phantom amounts beyond asOf.
 *
 * Optional `credits` (payments): [{ paidAt, amount }] reduce the
 * interest-bearing balance from their receipt date (including unreconciled).
 */
export const calculateAccruedInterest = (
  principal,
  dueDate,
  asOf = new Date(),
  credits = []
) => {
  const amount = Number(principal) || 0;
  if (!(amount > 0) || !dueDate) return 0;
  const due = parseDateOnly(dueDate);
  const asOfDate = parseDateOnly(asOf) || new Date();
  if (!due) return 0;
  const daysPastDue = daysBetween(due, asOfDate);
  if (daysPastDue == null || daysPastDue < INTEREST_GRACE_DAYS) return 0;

  const normalizedCredits = (credits || [])
    .map((c) => ({
      paidAt: parseDateOnly(c.paidAt || c.date),
      amount: Math.max(0, Number(c.amount) || Number(c.amountCAD) || 0),
    }))
    .filter((c) => c.paidAt && c.amount > 0)
    .sort((a, b) => a.paidAt - b.paidAt);

  // No credits → original flat simple interest on full principal
  if (!normalizedCredits.length) {
    const months =
      Math.floor((daysPastDue - INTEREST_GRACE_DAYS) / INTEREST_GRACE_DAYS) + 1;
    if (months <= 0) return 0;
    return Math.round(amount * MONTHLY_INTEREST_RATE * months * 100) / 100;
  }

  // Declining-balance monthly: each 30-day interest period after grace
  let total = 0;
  let periodStart = addDaysToDate(due, INTEREST_GRACE_DAYS);
  while (periodStart.getTime() <= asOfDate.getTime()) {
    const creditSum = normalizedCredits
      .filter((c) => c.paidAt.getTime() <= periodStart.getTime())
      .reduce((s, c) => s + c.amount, 0);
    const balance = Math.max(0, amount - creditSum);
    if (balance > 0) {
      total += balance * MONTHLY_INTEREST_RATE;
    }
    periodStart = addDaysToDate(periodStart, INTEREST_GRACE_DAYS);
  }
  return Math.round(total * 100) / 100;
};

/** Parse JSON-ish Airtable long-text fields safely. */
export const parseJsonField = (raw, fallback = null) => {
  if (raw == null || raw === "") return fallback;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(String(raw));
  } catch {
    return fallback;
  }
};

export const parseArEffectiveStart = (raw) => {
  const parsed = parseJsonField(raw, null);
  if (!parsed) return null;
  if (typeof parsed === "string") {
    const date = parseDateOnly(parsed);
    return date
      ? {
          date: date.toISOString().slice(0, 10),
          setBy: null,
          setAt: null,
          note: null,
        }
      : null;
  }
  const date = parseDateOnly(parsed.date || parsed.effectiveStart);
  if (!date) return null;
  return {
    date: date.toISOString().slice(0, 10),
    setBy: parsed.setBy || parsed.who || null,
    setAt: parsed.setAt || parsed.when || null,
    note: parsed.note || null,
  };
};

export const buildArEffectiveStartAudit = (
  date,
  { setBy = null, note = null, setAt = new Date() } = {}
) => {
  const d = parseDateOnly(date);
  if (!d) return null;
  return {
    date: d.toISOString().slice(0, 10),
    setBy: setBy || null,
    setAt:
      setAt instanceof Date
        ? setAt.toISOString()
        : String(setAt || new Date().toISOString()),
    note: note ? String(note).trim() : null,
  };
};

export const addMonthsToDate = (date, months) => {
  const d = date instanceof Date ? new Date(date) : new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + Number(months || 0));
  // Clamp month-end overflow (Jan 31 + 1m → Mar 3 on some engines)
  if (d.getDate() < day) {
    d.setDate(0);
  }
  return d;
};

/**
 * Awarded date for payment-plan start:
 * 1. EstHearBackDate (awarded), else
 * 2. Applied date (EstCompletionDate / SentDate / Date) + 90 days.
 */
export const resolveAwardedDateForPlan = (opportunityOrFields = {}) => {
  const fields =
    opportunityOrFields?.fields ||
    getOpportunityFields(opportunityOrFields) ||
    opportunityOrFields ||
    {};
  const applied = parseDateOnly(
    fields.EstCompletionDate ||
      fields.SentDate ||
      fields.Date ||
      fields.appliedDate
  );
  const awarded = parseDateOnly(fields.EstHearBackDate || fields.awardedDate);
  if (awarded) {
    return {
      date: awarded,
      source: "awarded",
      appliedDate: applied,
    };
  }
  if (applied) {
    const inferred = addDaysToDate(applied, AWARDED_NET_DAYS);
    return {
      date: inferred,
      source: "applied+90d",
      appliedDate: applied,
    };
  }
  return { date: null, source: null, appliedDate: null };
};

export const buildPaymentPlanSchedule = ({
  principal,
  months = DEFAULT_PAYMENT_PLAN_MONTHS,
  startDate = new Date(),
  setBy = null,
  setAt = new Date(),
  awardedSource = null,
} = {}) => {
  const total = Math.max(0, Number(principal) || 0);
  const n = PAYMENT_PLAN_MONTH_OPTIONS.includes(Number(months))
    ? Number(months)
    : DEFAULT_PAYMENT_PLAN_MONTHS;
  const start = parseDateOnly(startDate) || new Date();
  const startIso = start.toISOString().slice(0, 10);
  if (!(total > 0) || n <= 0) {
    return {
      months: n,
      startDate: startIso,
      principal: total,
      installments: [],
      immediate: n === 1,
      awardedSource: awardedSource || null,
      setBy,
      setAt:
        setAt instanceof Date ? setAt.toISOString() : String(setAt || ""),
    };
  }
  const base = Math.floor((total / n) * 100) / 100;
  const installments = [];
  let allocated = 0;
  for (let i = 0; i < n; i += 1) {
    const due = addMonthsToDate(start, i);
    const amount =
      i === n - 1
        ? Math.round((total - allocated) * 100) / 100
        : base;
    allocated = Math.round((allocated + amount) * 100) / 100;
    installments.push({
      index: i + 1,
      dueDate: due.toISOString().slice(0, 10),
      amount,
      paidAt: null,
      paidAmount: 0,
    });
  }
  return {
    months: n,
    startDate: startIso,
    principal: total,
    installments,
    immediate: n === 1,
    awardedSource: awardedSource || null,
    setBy: setBy || null,
    setAt:
      setAt instanceof Date ? setAt.toISOString() : String(setAt || ""),
  };
};

/**
 * Build a plan whose first installment starts on the resolved awarded date.
 */
export const buildPaymentPlanFromAwardedDate = (
  opportunityOrInvoice,
  {
    principal = null,
    months = DEFAULT_PAYMENT_PLAN_MONTHS,
    setBy = null,
  } = {}
) => {
  // Prefer precomputed plan dates from invoice rows when present
  if (opportunityOrInvoice?.planAwardedDate) {
    const fee =
      principal != null
        ? Number(principal)
        : Number(opportunityOrInvoice?.feeOwed) || 0;
    return buildPaymentPlanSchedule({
      principal: fee,
      months,
      startDate: opportunityOrInvoice.planAwardedDate,
      setBy,
      awardedSource: opportunityOrInvoice.planAwardedSource || null,
    });
  }

  const fields =
    opportunityOrInvoice?.opportunity ||
    opportunityOrInvoice?.fields ||
    opportunityOrInvoice ||
    {};
  // Also accept flattened awardedDate / appliedDate on the invoice row
  const merged = {
    ...(fields.fields || fields),
    EstHearBackDate:
      fields.EstHearBackDate ||
      fields.awardedDate ||
      opportunityOrInvoice?.awardedDate,
    EstCompletionDate:
      fields.EstCompletionDate ||
      fields.appliedDate ||
      opportunityOrInvoice?.appliedDate,
  };
  const resolved = resolveAwardedDateForPlan(merged);
  const fee =
    principal != null
      ? Number(principal)
      : Number(opportunityOrInvoice?.feeOwed) || 0;
  if (!resolved.date) {
    return null;
  }
  return buildPaymentPlanSchedule({
    principal: fee,
    months,
    startDate: resolved.date,
    setBy,
    awardedSource: resolved.source,
  });
};

export const parsePaymentPlan = (raw) => {
  const parsed = parseJsonField(raw, null);
  if (!parsed || typeof parsed !== "object") return null;
  const months = Number(parsed.months) || DEFAULT_PAYMENT_PLAN_MONTHS;
  const principal = Number(parsed.principal) || 0;
  const startDate =
    parseDateOnly(parsed.startDate)?.toISOString().slice(0, 10) || null;
  let installments = Array.isArray(parsed.installments)
    ? parsed.installments.map((inst, i) => ({
        index: Number(inst.index) || i + 1,
        dueDate:
          parseDateOnly(inst.dueDate)?.toISOString().slice(0, 10) || null,
        amount: Math.max(0, Number(inst.amount) || 0),
        paidAt: inst.paidAt
          ? parseDateOnly(inst.paidAt)?.toISOString().slice(0, 10) || null
          : null,
        paidAmount: Math.max(0, Number(inst.paidAmount) || 0),
      }))
    : [];
  if (!installments.length && principal > 0 && startDate) {
    return buildPaymentPlanSchedule({
      principal,
      months,
      startDate,
      setBy: parsed.setBy,
      setAt: parsed.setAt,
    });
  }
  return {
    months,
    startDate,
    principal,
    installments,
    setBy: parsed.setBy || null,
    setAt: parsed.setAt || null,
  };
};

export const getUnpaidInstallmentTotal = (plan) => {
  if (!plan?.installments?.length) return 0;
  return (
    Math.round(
      plan.installments.reduce((sum, inst) => {
        const unpaid = Math.max(
          0,
          (Number(inst.amount) || 0) - (Number(inst.paidAmount) || 0)
        );
        return sum + unpaid;
      }, 0) * 100
    ) / 100
  );
};

const formatDueBucketDate = (value) => {
  const d = value instanceof Date ? value : parseDateOnly(value);
  if (!d) return null;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/** Caption under the invoice “Coming due later” line. */
export const DUE_LATER_BRIEF =
  "Unpaid payment-plan installments and retainers scheduled after today, plus their share of tax. Payments apply to Due today first.";

export const describeDueLaterNote = ({
  laterDates = [],
  laterPlanCount = 0,
  laterRetainerCount = 0,
} = {}) => {
  const dates = [...laterDates]
    .map((d) => (d instanceof Date ? d : parseDateOnly(d)))
    .filter(Boolean)
    .sort((a, b) => a.getTime() - b.getTime());
  const remaining = dates.length;
  const nextLabel = remaining ? formatDueBucketDate(dates[0]) : null;
  const taxAndPay =
    "Includes their share of tax. Payments apply to Due today first.";

  if (laterPlanCount && !laterRetainerCount) {
    if (remaining === 1 && nextLabel) {
      return `Unpaid installment due ${nextLabel}. ${taxAndPay}`;
    }
    if (remaining > 1 && nextLabel) {
      return `${remaining} unpaid installments after today (next ${nextLabel}). ${taxAndPay}`;
    }
    return `Unpaid payment-plan installments scheduled after today. ${taxAndPay}`;
  }

  if (laterRetainerCount && !laterPlanCount) {
    if (remaining === 1 && nextLabel) {
      return `Retainer scheduled for ${nextLabel}. ${taxAndPay}`;
    }
    if (remaining > 1 && nextLabel) {
      return `${remaining} retainers scheduled after today (next ${nextLabel}). ${taxAndPay}`;
    }
    return `Retainers scheduled after today. ${taxAndPay}`;
  }

  if (nextLabel) {
    return remaining === 1
      ? `Scheduled after today (due ${nextLabel}). ${taxAndPay}`
      : `Scheduled after today (${remaining} items, next ${nextLabel}). ${taxAndPay}`;
  }

  return DUE_LATER_BRIEF;
};

/**
 * Split unpaid plan principal into amounts already due vs still scheduled.
 * Due-date day counts as due today.
 */
export const getInstallmentDueBuckets = (plan, asOf = new Date()) => {
  const empty = { dueToday: 0, dueLater: 0, laterDates: [] };
  if (!plan?.installments?.length) return empty;
  const asOfDate = parseDateOnly(asOf) || new Date();
  let dueToday = 0;
  let dueLater = 0;
  const laterDates = [];
  for (const inst of plan.installments) {
    const unpaid = Math.max(
      0,
      (Number(inst.amount) || 0) - (Number(inst.paidAmount) || 0)
    );
    if (!(unpaid > 0)) continue;
    const due = parseDateOnly(inst.dueDate);
    if (!due || due.getTime() <= asOfDate.getTime()) {
      dueToday += unpaid;
    } else {
      dueLater += unpaid;
      laterDates.push(due);
    }
  }
  return {
    dueToday: Math.round(dueToday * 100) / 100,
    dueLater: Math.round(dueLater * 100) / 100,
    laterDates,
  };
};

const lineItemScheduleIdentity = (item = {}) => {
  const upgradeName = String(item.upgradeName || "").trim();
  const opportunityName = String(item.opportunityName || "").trim();
  const description = String(item.description || "").trim();
  const label = opportunityName || upgradeName || description || "Line";
  const amount = Math.max(0, Number(item.amount) || 0);
  const planMonths = Number(item.paymentPlan?.months) || 0;
  return {
    groupKey: String(item.id || label),
    label,
    upgradeName,
    opportunityName,
    description,
    awarded: Math.max(0, Number(item.awarded || item.recovered) || 0),
    feeAmount: amount,
    feeNote: item.note || null,
    feePercent:
      item.feePercent != null && item.feePercent !== ""
        ? Number(item.feePercent)
        : null,
    appliedDate: item.appliedDate || null,
    awardedDate: item.awardedDate || null,
    cashReceivedDate: item.cashReceivedDate || null,
    termsDueDate: item.dueDate || null,
    hoursBased: Boolean(item.hoursBased || item.feeHoursBased),
    quantity: Number(item.quantity) || null,
    unitAmount: Number(item.unitAmount) || null,
    kind: item.kind || "fee",
    planMonths,
    monthLabel: item.monthLabel || null,
  };
};

const sortScheduleRowsByDueDate = (rows = []) =>
  [...rows].sort((a, b) => {
    const da = parseDateOnly(a.dueDate);
    const db = parseDateOnly(b.dueDate);
    if (da && db) return da.getTime() - db.getTime();
    if (da) return -1;
    if (db) return 1;
    return 0;
  });

const scheduleRowsForLineItem = (item, asOfDate) => {
  const amount = Math.max(0, Number(item.amount) || 0);
  if (!(amount > 0)) return [];
  const identity = lineItemScheduleIdentity(item);
  const plan = item.paymentPlan || null;
  const months = Number(plan?.months) || 0;
  const withIdentity = (row) => ({ ...identity, ...row });

  if (plan?.installments?.length && months > 1) {
    return plan.installments.map((inst) => {
      const instAmount = Math.max(0, Number(inst.amount) || 0);
      const paid = Math.max(0, Number(inst.paidAmount) || 0);
      const unpaid = Math.max(0, Math.round((instAmount - paid) * 100) / 100);
      const paidOff = unpaid <= 0.009;
      const due = parseDateOnly(inst.dueDate);
      const bucket = paidOff
        ? "paid"
        : !due || due.getTime() <= asOfDate.getTime()
          ? "today"
          : "later";
      return withIdentity({
        key: `${identity.groupKey}-inst-${inst.index}`,
        opportunity: identity.label,
        detail: `Installment ${inst.index} of ${months}`,
        dueDate: inst.dueDate || null,
        amount: instAmount,
        unpaid: paidOff ? 0 : unpaid,
        bucket,
      });
    });
  }

  if (item.kind === "retainer") {
    const due = parseDateOnly(item.dueDate);
    return [
      withIdentity({
        key: item.id || `retainer-${item.dueDate}`,
        opportunity: item.description || "Retainer",
        detail: item.monthLabel
          ? `Retainer · ${item.monthLabel}`
          : "Retainer",
        dueDate: item.dueDate || null,
        amount,
        unpaid: amount,
        bucket: !due || due.getTime() <= asOfDate.getTime() ? "today" : "later",
      }),
    ];
  }

  return [
    withIdentity({
      key: identity.groupKey,
      opportunity: identity.label,
      detail: "Payable with this statement",
      dueDate: item.dueDate || null,
      amount,
      unpaid: amount,
      bucket: "today",
    }),
  ];
};

/**
 * Client-facing payment schedule rows for a statement (installments, retainers,
 * or the full fee if there is no multi-month plan).
 */
export const buildDueScheduleRows = (lineItems = [], asOf = new Date()) => {
  const asOfDate = parseDateOnly(asOf) || parseDateOnly(new Date());
  const rows = [];
  for (const item of lineItems || []) {
    rows.push(...scheduleRowsForLineItem(item, asOfDate));
  }
  return sortScheduleRowsByDueDate(rows);
};

/**
 * Same schedule, grouped by opportunity so clients can see grant name,
 * awarded amount, applied/awarded dates, then each installment.
 */
export const buildDueScheduleGroups = (lineItems = [], asOf = new Date()) => {
  const asOfDate = parseDateOnly(asOf) || parseDateOnly(new Date());
  const groups = [];
  for (const item of lineItems || []) {
    const rows = sortScheduleRowsByDueDate(
      scheduleRowsForLineItem(item, asOfDate)
    );
    if (!rows.length) continue;
    const identity = lineItemScheduleIdentity(item);
    groups.push({
      ...identity,
      key: identity.groupKey,
      rows,
    });
  }
  return groups;
};

/**
 * Invoice-level due-today vs scheduled-later buckets.
 * Only meaningful when a multi-month plan (or future retainer) leaves $ later.
 * Payments apply to due-today first, then scheduled amounts.
 */
export const resolveInvoiceDueBuckets = ({
  lineItems = [],
  taxTotal = 0,
  interestAccrued = 0,
  paidToDate = null,
  asOf = new Date(),
} = {}) => {
  const asOfDate = parseDateOnly(asOf) || new Date();
  let principalToday = 0;
  let principalLater = 0;
  const laterDates = [];
  let laterPlanCount = 0;
  let laterRetainerCount = 0;

  for (const item of lineItems || []) {
    const amount = Math.max(0, Number(item.amount) || 0);
    if (!(amount > 0)) continue;

    const plan = item.paymentPlan
      ? parsePaymentPlan(item.paymentPlan) || item.paymentPlan
      : null;
    const months = Number(plan?.months) || 0;

    if (plan?.installments?.length && months > 1) {
      const buckets = getInstallmentDueBuckets(plan, asOfDate);
      principalToday += buckets.dueToday;
      principalLater += buckets.dueLater;
      laterDates.push(...(buckets.laterDates || []));
      laterPlanCount += buckets.laterDates?.length || 0;
      continue;
    }

    if (item.kind === "retainer" && item.dueDate) {
      const due = parseDateOnly(item.dueDate);
      if (due && due.getTime() > asOfDate.getTime()) {
        principalLater += amount;
        laterDates.push(due);
        laterRetainerCount += 1;
      } else {
        principalToday += amount;
      }
      continue;
    }

    // Immediate / no plan — payable with this invoice
    principalToday += amount;
  }

  principalToday = Math.round(principalToday * 100) / 100;
  principalLater = Math.round(principalLater * 100) / 100;
  const principalTotal = principalToday + principalLater;
  const tax = Math.max(0, Number(taxTotal) || 0);
  const interest = Math.max(0, Number(interestAccrued) || 0);

  const taxToday =
    principalTotal > 0
      ? Math.round(((tax * principalToday) / principalTotal) * 100) / 100
      : tax;
  const taxLater = Math.round((tax - taxToday) * 100) / 100;

  // Interest is always collectible now. Keep the principal/tax amount separate
  // so the temporary no-interest payment link never includes future installments.
  const grossTodayWithoutInterest =
    Math.round((principalToday + taxToday) * 100) / 100;
  let grossToday =
    Math.round((principalToday + taxToday + interest) * 100) / 100;
  let grossLater = Math.round((principalLater + taxLater) * 100) / 100;

  const paid =
    paidToDate != null && paidToDate !== ""
      ? Math.max(0, Number(paidToDate) || 0)
      : 0;
  const appliedToToday = Math.min(paid, grossToday);
  const dueToday = Math.round((grossToday - appliedToToday) * 100) / 100;
  const dueTodayWithoutInterest =
    Math.round(Math.max(0, grossTodayWithoutInterest - paid) * 100) / 100;
  const dueLater = Math.round(
    Math.max(0, grossLater - Math.max(0, paid - appliedToToday)) * 100
  ) / 100;
  const hasFutureDue = dueLater > 0.009;
  const dueLaterNote = describeDueLaterNote({
    laterDates,
    laterPlanCount,
    laterRetainerCount,
  });

  return {
    dueToday,
    dueTodayWithoutInterest,
    dueLater,
    dueLaterNote,
    hasFutureDue,
    // Only surface the split when something is still scheduled later
    showDueToday: hasFutureDue,
  };
};

/** True when every installment due before asOf is fully paid (due day still counts as on schedule). */
export const isPaymentPlanOnSchedule = (plan, asOf = new Date()) => {
  if (!plan?.installments?.length) return false;
  const asOfDate = parseDateOnly(asOf) || new Date();
  return plan.installments.every((inst) => {
    const due = parseDateOnly(inst.dueDate);
    if (!due || due.getTime() >= asOfDate.getTime()) return true;
    const unpaid =
      (Number(inst.amount) || 0) - (Number(inst.paidAmount) || 0);
    return unpaid <= 0.009;
  });
};

/**
 * Interest while on a payment plan: suspended if on schedule; otherwise each
 * missed installment accrues from its own scheduled due date (+ 30d grace).
 * Shared credits are FIFO-allocated across installments by due date.
 */
export const calculatePaymentPlanInterest = (
  plan,
  asOf = new Date(),
  credits = []
) => {
  if (!plan?.installments?.length) return 0;
  if (isPaymentPlanOnSchedule(plan, asOf)) return 0;
  const obligations = plan.installments
    .map((inst, i) => ({
      key: `inst-${inst.index || i + 1}`,
      principal: Math.max(
        0,
        (Number(inst.amount) || 0) - (Number(inst.paidAmount) || 0)
      ),
      dueDate: inst.dueDate,
    }))
    .filter((o) => o.principal > 0);
  if (!obligations.length) return 0;
  const allocated = allocateCreditsFifo(obligations, credits);
  return (
    Math.round(
      obligations.reduce((sum, o) => {
        return (
          sum +
          calculateAccruedInterest(
            o.principal,
            o.dueDate,
            asOf,
            allocated[o.key] || []
          )
        );
      }, 0) * 100
    ) / 100
  );
};

/**
 * Resolve fee interest for an opportunity: payment-plan rules when a plan
 * exists; otherwise principal interest from due date with payment credits.
 */
export const resolveFeeInterest = ({
  feeOwed,
  dueDate,
  paymentPlan = null,
  asOf = new Date(),
  credits = [],
} = {}) => {
  const plan = paymentPlan ? parsePaymentPlan(paymentPlan) || paymentPlan : null;
  if (plan?.installments?.length) {
    return calculatePaymentPlanInterest(plan, asOf, credits);
  }
  return calculateAccruedInterest(feeOwed, dueDate, asOf, credits);
};

const isSuccessFeePaymentValue = (value) => {
  if (value == null || value === "") return false;
  if (value === false || value === 0 || value === "0") return false;
  return !["false", "no", "off"].includes(String(value).trim().toLowerCase());
};

const isPtdPaymentValue = (value) => {
  if (value == null || value === "") return true;
  if (value === false || value === 0 || value === "0") return false;
  return !["false", "no", "off"].includes(String(value).trim().toLowerCase());
};

/** Payments that change interest-bearing balance (posted, including unmatched + refunds). */
export const paymentsAsInterestCredits = (payments = []) =>
  (payments || [])
    .filter((p) => {
      const status = String(p.status || "posted").toLowerCase();
      if (status === "void" || status === "ignored") return false;
      if (!isPtdPaymentValue(p.countsTowardPTD ?? p.CountsTowardPTD)) {
        return false;
      }
      const successFeeValue =
        p.countsTowardSuccessFee ?? p.CountsTowardSuccessFee;
      if (!isSuccessFeePaymentValue(successFeeValue)) return false;
      const amount = Number(p.amountCAD ?? p.amount) || 0;
      return amount !== 0 && (p.paidAt || p.date);
    })
    .map((p) => ({
      paidAt: p.paidAt || p.date,
      amount: Number(p.amountCAD ?? p.amount) || 0,
      unreconciled:
        String(p.matchConfidence || "").toLowerCase() === "unmatched" ||
        Boolean(p.unreconciled),
      id: p.id || null,
    }));

/**
 * FIFO-allocate payment credits onto obligations ordered by terms due date
 * (oldest due first). Each dollar of the Payments ledger hits only one line.
 * Negative amounts (refunds) increase remaining on the oldest open line.
 */
export const allocateCreditsFifo = (obligations = [], credits = []) => {
  const rows = (obligations || []).map((o, i) => ({
    key: String(o.key ?? o.id ?? `obl-${i}`),
    dueDate: parseDateOnly(o.dueDate),
    remaining: Math.max(0, Number(o.principal) || 0),
    allocated: [],
  }));

  const order = rows
    .map((_, i) => i)
    .sort((a, b) => {
      const da = rows[a].dueDate?.getTime() ?? Number.POSITIVE_INFINITY;
      const db = rows[b].dueDate?.getTime() ?? Number.POSITIVE_INFINITY;
      if (da !== db) return da - db;
      return a - b;
    });

  const normalized = (credits || [])
    .map((c) => ({
      paidAt: parseDateOnly(c.paidAt || c.date),
      amount: Number(c.amount) || Number(c.amountCAD) || 0,
    }))
    .filter((c) => c.paidAt && c.amount !== 0)
    .sort((a, b) => a.paidAt - b.paidAt);

  for (const credit of normalized) {
    if (credit.amount > 0) {
      let left = credit.amount;
      for (const idx of order) {
        if (left <= 0) break;
        const row = rows[idx];
        if (!(row.remaining > 0)) continue;
        const apply = Math.min(left, row.remaining);
        row.allocated.push({
          paidAt: credit.paidAt.toISOString().slice(0, 10),
          amount: Math.round(apply * 100) / 100,
        });
        row.remaining = Math.round((row.remaining - apply) * 100) / 100;
        left = Math.round((left - apply) * 100) / 100;
      }
    } else {
      // Refund: restore principal on the oldest due obligation
      const add = Math.abs(credit.amount);
      const idx = order[0];
      if (idx == null) continue;
      const row = rows[idx];
      row.remaining = Math.round((row.remaining + add) * 100) / 100;
      row.allocated.push({
        paidAt: credit.paidAt.toISOString().slice(0, 10),
        amount: -Math.round(add * 100) / 100,
      });
    }
  }

  const byKey = {};
  rows.forEach((r) => {
    byKey[r.key] = r.allocated;
  });
  return byKey;
};

/**
 * Per-line interest from each obligation's payment terms → asOf, after
 * reconciling the shared Payments ledger (FIFO by due date).
 *
 * @param {Array<{ key?: string, id?: string, principal: number, dueDate: string|Date|null, paymentPlan?: object|null }>} lines
 * @returns {Record<string, number>} interest by line key
 */
export const calculateLineInterestsWithPayments = ({
  lines = [],
  payments = null,
  interestCredits = null,
  asOf = new Date(),
} = {}) => {
  const credits =
    interestCredits != null
      ? interestCredits
      : paymentsAsInterestCredits(payments || []);

  const obligations = (lines || []).map((l, i) => ({
    key: String(l.key ?? l.id ?? `line-${i}`),
    principal: Math.max(0, Number(l.principal ?? l.amount ?? l.feeOwed) || 0),
    dueDate: l.dueDate || null,
    paymentPlan: l.paymentPlan || null,
  }));

  const allocated = allocateCreditsFifo(obligations, credits);
  const interestByKey = {};
  for (const o of obligations) {
    interestByKey[o.key] = resolveFeeInterest({
      feeOwed: o.principal,
      dueDate: o.dueDate,
      paymentPlan: o.paymentPlan,
      asOf,
      credits: allocated[o.key] || [],
    });
  }
  return interestByKey;
};

/** Due date for a retainer month line: invoice/as-of date + Net 15. */
export const getRetainerDueDate = (invoiceDate, netTermsDays = CASH_RECEIVED_NET_DAYS) => {
  const d = parseDateOnly(invoiceDate) || new Date();
  return addDaysToDate(d, netTermsDays);
};

/**
 * Expand a retainer range into per-month invoice lines.
 * Inclusive of start and end month (day clamped to 1st of each month).
 */
export const buildRetainerLinesFromRange = ({
  monthlyAmount,
  startDate,
  endDate,
} = {}) => {
  const amount = Math.max(0, Number(monthlyAmount) || 0);
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (!(amount > 0) || !start || !end || end < start) return [];

  const lines = [];
  let cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  let guard = 0;
  while (cursor <= endMonth && guard < 120) {
    const invoiceDate = cursor.toISOString().slice(0, 10);
    const monthLabel = cursor.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
    lines.push({
      id: `retainer-${invoiceDate}`,
      monthLabel,
      amount,
      invoiceDate,
    });
    cursor = addMonthsToDate(cursor, 1);
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    guard += 1;
  }
  return lines;
};

/**
 * Infer { monthlyAmount, startDate, endDate } from stored lines or range object.
 */
export const retainerRangeFromStored = (raw) => {
  const empty = {
    monthlyAmount: "",
    startDate: "",
    endDate: "",
    excludedIds: [],
  };
  if (raw == null || raw === "") return empty;

  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return empty;
    }
  }

  if (parsed && !Array.isArray(parsed) && typeof parsed === "object") {
    if (parsed.monthlyAmount != null || parsed.startDate || parsed.endDate) {
      const excludedIds = Array.isArray(parsed.excludedIds)
        ? parsed.excludedIds.map(String).filter(Boolean)
        : [];
      return {
        monthlyAmount:
          parsed.monthlyAmount != null && parsed.monthlyAmount !== ""
            ? String(parsed.monthlyAmount)
            : "",
        startDate: parsed.startDate
          ? String(parsed.startDate).slice(0, 10)
          : "",
        endDate: parsed.endDate ? String(parsed.endDate).slice(0, 10) : "",
        excludedIds,
      };
    }
  }

  const lines = Array.isArray(parsed) ? parsed : [];
  if (!lines.length) return empty;
  const amounts = lines.map((l) => Number(l.amount) || 0).filter((a) => a > 0);
  const dates = lines
    .map((l) => parseDateOnly(l.invoiceDate)?.toISOString().slice(0, 10))
    .filter(Boolean)
    .sort();
  const excludedIds = lines
    .filter((l) => l.excludedFromTotal || l.excluded)
    .map((l) => String(l.id || `retainer-${l.invoiceDate}`))
    .filter(Boolean);
  return {
    monthlyAmount: amounts.length ? String(amounts[0]) : "",
    startDate: dates[0] || "",
    endDate: dates[dates.length - 1] || "",
    excludedIds,
  };
};

/**
 * Normalize missed retainer month rows for invoice docs.
 * Accepts an array of lines, or a range { monthlyAmount, startDate, endDate, excludedIds }.
 * Excluded months stay listed but do not bill (amount/interest = 0 for totals).
 */
export const normalizeRetainerLines = (rows = [], asOf = new Date()) => {
  const asOfDate = parseDateOnly(asOf) || new Date();
  const asOfIso = asOfDate.toISOString().slice(0, 10);

  let source = rows;
  let excludedIds = [];
  if (
    rows &&
    !Array.isArray(rows) &&
    typeof rows === "object" &&
    (rows.monthlyAmount != null || rows.startDate || rows.endDate)
  ) {
    excludedIds = Array.isArray(rows.excludedIds)
      ? rows.excludedIds.map(String)
      : [];
    source = buildRetainerLinesFromRange(rows);
  } else if (Array.isArray(rows)) {
    excludedIds = rows
      .filter((r) => r?.excludedFromTotal || r?.excluded)
      .map((r) => String(r.id || ""))
      .filter(Boolean);
  }
  const excludedSet = new Set(excludedIds);

  return (source || [])
    .map((row, index) => {
      const listedAmount = Math.max(0, Number(row.amount) || 0);
      if (!(listedAmount > 0)) return null;
      const monthLabel =
        String(row.monthLabel || row.label || row.month || "").trim() ||
        `Month ${index + 1}`;
      const invoiceDate =
        parseDateOnly(row.invoiceDate)?.toISOString().slice(0, 10) || asOfIso;
      const id = row.id || `retainer-${invoiceDate}`;
      const excludedFromTotal =
        Boolean(row.excludedFromTotal || row.excluded) || excludedSet.has(id);
      const amount = excludedFromTotal ? 0 : listedAmount;
      const due = getRetainerDueDate(invoiceDate);
      const dueDate = due.toISOString().slice(0, 10);
      const interestAccrued = excludedFromTotal
        ? 0
        : calculateAccruedInterest(listedAmount, due, asOfDate);
      return {
        id,
        kind: "retainer",
        description: `Retainer — ${monthLabel}`,
        monthLabel,
        invoiceDate,
        dueDate,
        awarded: 0,
        recovered: 0,
        hoursBased: false,
        feeHoursBased: false,
        memo: excludedFromTotal
          ? `Listed · not billed · Net ${CASH_RECEIVED_NET_DAYS} from ${invoiceDate}`
          : `Net ${CASH_RECEIVED_NET_DAYS} from ${invoiceDate}`,
        quantity: 1,
        unitAmount: listedAmount,
        amount,
        listedAmount,
        excludedFromTotal,
        interestAccrued,
        note: excludedFromTotal ? "Not billed" : null,
      };
    })
    .filter(Boolean);
};

export const parseMissedRetainerMonthsField = (raw) => {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(String(raw));
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === "object") {
      // Preserve range + excludedIds for normalizeRetainerLines
      return parsed;
    }
    return [];
  } catch {
    return [];
  }
};

export const getDaysOverdue = (
  opportunity,
  asOf = new Date(),
  netTermsDays = DEFAULT_NET_TERMS_DAYS,
  companyFields = null
) => {
  const due = getInvoiceDueDate(opportunity, netTermsDays, companyFields);
  if (!due) return null;
  const today = parseDateOnly(asOf) || new Date();
  return daysBetween(due, today);
};

export const getArStage = (daysOverdue) => {
  if (daysOverdue == null) {
    return AR_STAGES.find((s) => s.id === "current");
  }
  return (
    AR_STAGES.find(
      (s) => daysOverdue >= s.minDays && daysOverdue <= s.maxDays
    ) || AR_STAGES[AR_STAGES.length - 1]
  );
};

export const getNextInvoiceReminderDate = (
  daysOverdue,
  fromDate = new Date()
) => {
  const stage = getArStage(daysOverdue);
  const interval =
    INVOICE_REMINDER_INTERVAL_DAYS[stage?.id] ??
    INVOICE_FIRST_REMINDER_DAYS;
  return formatAirtableDate(addDaysToDate(fromDate, interval));
};

/**
 * Normalize an opportunity into an AR invoice row for panels / emails.
 */
export const buildInvoiceRecord = (
  opportunity,
  {
    feeRate = DEFAULT_SUCCESS_FEE_RATE,
    successFeePercent = null,
    teamShare = DEFAULT_TEAM_SHARE_OF_FEE,
    netTermsDays = DEFAULT_NET_TERMS_DAYS,
    asOf = new Date(),
    companies = null,
    companyFields = null,
    interestCredits = null,
    payments = null,
  } = {}
) => {
  const fields = getOpportunityFields(opportunity);
  const company = companyFields
    ? { fields: companyFields }
    : findCompanyForOpportunity(opportunity, companies);
  const companyFeePercent = resolveSuccessFeePercent(
    successFeePercent ??
      company?.fields?.SuccessFeePercent ??
      (feeRate != null && feeRate <= 1 ? feeRate * 100 : feeRate) ??
      DEFAULT_SUCCESS_FEE_PERCENT
  );
  const rate = getFeeRateForOpportunity(opportunity, {
    feeRate,
    successFeePercent:
      successFeePercent ??
      (company?.fields?.SuccessFeePercent != null
        ? company.fields.SuccessFeePercent
        : null),
    companies,
  });
  const status = fields.Status || "";
  const feeHoursBased = isCancelledArStatus(status);
  // Cancelled fees are hours-based — do not count AdjustedAmount as "awarded"
  const recovered = feeHoursBased ? 0 : getRecoveredAmount(opportunity);
  const billableHours = feeHoursBased ? getBillableHours(opportunity) : 0;
  const feeOwed = resolveFeeOwedAmount(opportunity, rate);
  const feePercent = rate * 100;
  const feePercentOverridden = getOpportunityFeePercentOverride(opportunity) != null;
  const feeDollarOverridden =
    fields.SuccessFeeAmount !== null &&
    fields.SuccessFeeAmount !== undefined &&
    String(fields.SuccessFeeAmount).trim() !== "";
  const feeOverridden =
    feePercentOverridden || feeDollarOverridden || feeHoursBased;
  const teamPayout = feeOwed * teamShare;
  const resolvedCompanyFields = company?.fields || companyFields || null;
  const daysOverdue = getDaysOverdue(
    opportunity,
    asOf,
    netTermsDays,
    resolvedCompanyFields
  );
  const stage = getArStage(daysOverdue);
  const open = isInvoiceOpen(opportunity, {
    feeRate: rate,
    companies,
  });
  const invoiceSentDate = fields.InvoiceSentDate || null;
  const dueDate = getInvoiceDueDate(
    opportunity,
    netTermsDays,
    resolvedCompanyFields
  );
  const awardedDate = fields.EstHearBackDate || null;
  const cashReceivedDate =
    fields[CLIENT_CASH_RECEIVED_FIELD] ||
    fields.CashReceivedDate ||
    resolvedCompanyFields?.[CLIENT_CASH_RECEIVED_FIELD] ||
    resolvedCompanyFields?.CashReceivedDate ||
    null;
  const effectiveStart = parseArEffectiveStart(
    fields[AR_EFFECTIVE_START_FIELD] || fields.ArEffectiveStartDate
  );
  const paymentPlan = parsePaymentPlan(fields[PAYMENT_PLAN_FIELD]);
  const planAwarded = resolveAwardedDateForPlan(fields);
  const credits =
    interestCredits != null
      ? interestCredits
      : paymentsAsInterestCredits(payments);
  const interestAccrued = resolveFeeInterest({
    feeOwed,
    dueDate,
    paymentPlan,
    asOf,
    credits,
  });
  const unpaidInstallments = paymentPlan
    ? getUnpaidInstallmentTotal(paymentPlan)
    : null;
  const planOnSchedule = paymentPlan
    ? isPaymentPlanOnSchedule(paymentPlan, asOf)
    : null;
  const collectedToDate = Number(fields.CollectedDollarsToDate);
  const tax = resolveInvoiceTax(feeOwed, company?.fields || {});

  return {
    id: opportunity.id || fields.id || null,
    opportunity,
    companyName:
      fields.CompanyName ||
      fields.Company ||
      opportunity.companyName ||
      company?.fields?.Name ||
      "—",
    companyId: fields.CompanyID || null,
    companyFields: company?.fields || null,
    opportunityName: String(fields.OpportunityName || "").trim(),
    upgradeName:
      String(fields.UpgradeName || "").trim() ||
      String(fields.Name || "").trim() ||
      "Opportunity",
    assignee: fields.Assignee || "",
    status,
    recovered,
    awardedDate,
    cashReceivedDate,
    effectiveStart,
    planAwardedDate: planAwarded.date
      ? planAwarded.date.toISOString().slice(0, 10)
      : null,
    planAwardedSource: planAwarded.source,
    appliedDate: planAwarded.appliedDate
      ? planAwarded.appliedDate.toISOString().slice(0, 10)
      : fields.EstCompletionDate || fields.SentDate || fields.Date || null,
    paymentPlan,
    planOnSchedule,
    unpaidInstallments,
    feeOwed,
    feePercent,
    companyFeePercent,
    feeOverridden,
    feePercentOverridden,
    feeDollarOverridden,
    feeHoursBased,
    billableHours,
    hourlyRate: feeHoursBased ? CANCELLED_HOURLY_RATE : null,
    teamPayout,
    tax,
    taxTotal: tax.taxTotal,
    interestAccrued,
    totalDue: Math.round((tax.totalWithTax + interestAccrued) * 100) / 100,
    collectedToDate: Number.isFinite(collectedToDate) ? collectedToDate : null,
    invoiceSentDate,
    dueDate: dueDate ? dueDate.toISOString().slice(0, 10) : null,
    daysOverdue,
    stage,
    open,
    paid: isInvoicePaid(opportunity),
    paidInvoiceBy: fields.PaidInvoiceBy || "",
    attentionPaidInvoiceBy: fields.AttentionPaidInvoiceBy || "",
    netTermsDays,
    invoiceNumber: fields.InvoiceNumber || opportunity.id?.slice(-6) || "—",
    paymentLink: fields.PaymentLink || fields.InvoiceLink || "",
  };
};

/**
 * Line item copy for success fee vs cancelled hourly billing.
 */
export const resolveInvoiceLineMeta = (invoice, { sameCompany = true } = {}) => {
  const upgradeLabel =
    String(invoice?.upgradeName || "").trim() || "Opportunity";
  const hours = Number(invoice?.billableHours) || 0;
  const hourlyRate =
    Number(invoice?.hourlyRate) || CANCELLED_HOURLY_RATE;
  const feeOwed = Number(invoice?.feeOwed) || 0;
  const hoursBased =
    Boolean(invoice?.feeHoursBased) || isCancelledArStatus(invoice?.status);

  if (hoursBased) {
    const hoursNote =
      hours > 0
        ? `${hours} hrs × $${hourlyRate}/hr`
        : `Hours × $${hourlyRate}/hr`;
    return {
      description: `Cancellation fee — ${upgradeLabel}`,
      note: invoice?.feeDollarOverridden ? "Custom fee $" : hoursNote,
      memo: hoursNote,
      quantity: hours > 0 ? hours : 1,
      unitAmount: hours > 0 ? hourlyRate : feeOwed,
    };
  }

  const feeNote = invoice?.feeDollarOverridden
    ? "Custom fee $"
    : invoice?.feePercentOverridden
      ? `${Number(invoice.feePercent || 0).toFixed(0)}% of awarded (custom)`
      : `${Number(invoice.feePercent || 0).toFixed(0)}% of awarded`;

  return {
    description: `Success fee — ${upgradeLabel}`,
    note: feeNote,
    memo: [
      !sameCompany ? invoice?.companyName : null,
      invoice?.recovered
        ? `Awarded ${formatArCurrency(invoice.recovered)}`
        : null,
      !sameCompany && invoice?.awardedDate ? invoice.awardedDate : null,
    ]
      .filter(Boolean)
      .join(" · ") || null,
    quantity: 1,
    unitAmount: feeOwed,
  };
};

/**
 * Printable / email-ready invoice document from an invoice record + company.
 */
const resolveInvoicePaidCredits = (totalDue, paidToDate, companyFields = {}) => {
  const raw =
    paidToDate != null && paidToDate !== ""
      ? paidToDate
      : companyFields?.PaidToDate;
  if (raw == null || raw === "") {
    return { paidToDate: null, balanceDue: null };
  }
  const paid = Math.max(0, Number(raw) || 0);
  const due = Number(totalDue) || 0;
  return {
    paidToDate: paid,
    balanceDue: Math.max(0, Math.round((due - paid) * 100) / 100),
  };
};

/** Sort payment rows earliest → latest; source is ignored. Missing dates sort last. */
export const sortPaymentLinesByPaidAt = (lines = []) =>
  [...lines].sort((a, b) => {
    const sa = normalizePaidAtISO(a.paidAt);
    const sb = normalizePaidAtISO(b.paidAt);
    if (sa && sb) {
      const cmp = sa.localeCompare(sb);
      if (cmp !== 0) return cmp;
    } else if (sa !== sb) {
      return sa ? -1 : 1;
    }
    return String(a.id || "").localeCompare(String(b.id || ""));
  });

/** Normalize Payments ledger rows for invoice / AR panel display. */
export const normalizeInvoicePaymentLines = (payments = []) =>
  sortPaymentLinesByPaidAt(
    (payments || []).map((p) => ({
      id: p.id,
      paidAt: normalizePaidAtISO(p.paidAt),
      amountCAD: Number(p.amountCAD) || 0,
      amountOriginal:
        p.amountOriginal != null ? Number(p.amountOriginal) : null,
      currency: p.currency || "cad",
      taxAmount: p.taxAmount != null ? Number(p.taxAmount) : null,
      source: p.source || null,
      kind: p.kind || null,
      status: p.status || "posted",
      countsTowardPTD: p.countsTowardPTD !== false,
      counterparty: p.counterparty || null,
      customerEmail: p.customerEmail || null,
      invoiceNumber: p.invoiceNumber || null,
      externalKey: p.externalKey || null,
      matchConfidence: p.matchConfidence || null,
      countsTowardSuccessFee:
        isSuccessFeePaymentValue(
          p.countsTowardSuccessFee ?? p.CountsTowardSuccessFee
        ),
    }))
  );

export const sumPaymentLinesCAD = (payments = []) =>
  Math.round(
    normalizeInvoicePaymentLines(payments).reduce(
      (sum, p) => sum + (Number(p.amountCAD) || 0),
      0
    ) * 100
  ) / 100;

export const sumSuccessFeePaymentLinesCAD = (payments = []) =>
  Math.round(
    normalizeInvoicePaymentLines(payments)
      .filter(
        (payment) =>
          !["void", "ignored"].includes(
            String(payment.status || "posted").toLowerCase()
          ) &&
          payment.countsTowardPTD !== false &&
          payment.countsTowardSuccessFee !== false
      )
      .reduce((sum, payment) => sum + (Number(payment.amountCAD) || 0), 0) * 100
  ) / 100;

export const buildInvoiceDocument = (
  invoice,
  {
    seller = DEFAULT_INVOICE_SELLER,
    companyFields = null,
    asOf = new Date(),
    paidToDate = null,
    payments = null,
    retainerLines = null,
    interestCredits = null,
    standalone = false,
  } = {}
) => {
  const company = companyFields || invoice.companyFields || {};
  const feeOwed = Number(invoice.feeOwed) || 0;
  const tax = invoice.tax || resolveInvoiceTax(feeOwed, company);
  // Invoice document is always dated when issued / previewed (today), not when
  // a prior send was recorded. Per-line due dates carry award/cash terms.
  const invoiceDate = (parseDateOnly(asOf) || new Date())
    .toISOString()
    .slice(0, 10);
  const billToName =
    company.Name ||
    company.CompanyName ||
    invoice.companyName ||
    "—";
  const billToLines = buildBillToLines(company, billToName);

  const upgradeLabel =
    String(invoice.upgradeName || "").trim() || "Opportunity";
  const opportunityLabel = String(invoice.opportunityName || "").trim();
  const lineMeta = resolveInvoiceLineMeta(invoice, { sameCompany: true });

  const interestPaymentCredits =
    standalone
      ? []
      : interestCredits != null
        ? interestCredits
        : payments != null
          ? paymentsAsInterestCredits(payments)
          : [];

  const retainer =
    standalone
      ? []
      : retainerLines != null
        ? normalizeRetainerLines(retainerLines, asOf)
        : normalizeRetainerLines(
            parseMissedRetainerMonthsField(company[MISSED_RETAINER_MONTHS_FIELD]),
            asOf
          );

  const includeFeeLine = feeOwed > 0 || retainer.length === 0;
  const planNote = invoice.paymentPlan?.months
    ? `${invoice.paymentPlan.months}-mo plan${
        invoice.planOnSchedule ? " · on schedule" : " · past due installments"
      }`
    : invoice.effectiveStart?.date
      ? `Effective start ${invoice.effectiveStart.date}${
          invoice.effectiveStart.setBy
            ? ` · set by ${invoice.effectiveStart.setBy}`
            : ""
        }`
      : null;

  // Always recompute interest from payment terms → now, reconciled to ledger.
  const interestObligations = [];
  if (includeFeeLine && feeOwed > 0) {
    interestObligations.push({
      key: String(invoice.id || "fee"),
      principal: feeOwed,
      dueDate: invoice.dueDate || null,
      paymentPlan: invoice.paymentPlan || null,
    });
  }
  retainer.forEach((r) => {
    if (r.excludedFromTotal) return;
    interestObligations.push({
      key: String(r.id),
      principal: r.listedAmount || r.amount,
      dueDate: r.dueDate || null,
      paymentPlan: null,
    });
  });
  const interestByKey = calculateLineInterestsWithPayments({
    lines: interestObligations,
    interestCredits: interestPaymentCredits,
    asOf,
  });
  const interestAccrued =
    includeFeeLine && feeOwed > 0
      ? interestByKey[String(invoice.id || "fee")] || 0
      : 0;
  const retainerWithInterest = retainer.map((r) => ({
    ...r,
    interestAccrued: r.excludedFromTotal
      ? 0
      : interestByKey[String(r.id)] || 0,
  }));
  const retainerSubtotal = retainerWithInterest.reduce(
    (s, r) => s + (r.amount || 0),
    0
  );
  const retainerInterest = retainerWithInterest.reduce(
    (s, r) => s + (r.interestAccrued || 0),
    0
  );

  const feeSubtotal = feeOwed + retainerSubtotal;
  // Tax on fees + retainer only — interest is not taxed
  const taxed =
    retainerSubtotal > 0
      ? resolveInvoiceTax(feeSubtotal, company)
      : tax;
  const totalInterest =
    Math.round((interestAccrued + retainerInterest) * 100) / 100;
  const totalDue =
    Math.round((taxed.totalWithTax + totalInterest) * 100) / 100;

  const paymentLines = standalone
    ? []
    : normalizeInvoicePaymentLines(payments);
  const paymentsLedgerSum = paymentLines.length
    ? sumPaymentLinesCAD(paymentLines)
    : null;
  const successFeePaymentsLedgerSum =
    !standalone && payments != null
      ? sumSuccessFeePaymentLinesCAD(paymentLines)
      : null;
  const effectivePaid = standalone
    ? null
    : successFeePaymentsLedgerSum != null
      ? successFeePaymentsLedgerSum
      : company?.SuccessFeePaidToDate != null &&
          company?.SuccessFeePaidToDate !== ""
        ? company.SuccessFeePaidToDate
        : paidToDate != null && paidToDate !== ""
          ? paidToDate
          : company?.PaidToDate != null && company?.PaidToDate !== ""
            ? company.PaidToDate
            : paymentsLedgerSum;
  const credits = standalone
    ? { paidToDate: null, balanceDue: null }
    : resolveInvoicePaidCredits(totalDue, effectivePaid, company);

  const feeLineItems = includeFeeLine
    ? [
        {
          id: invoice.id,
          description: lineMeta.description,
          upgradeName: upgradeLabel,
          opportunityName: opportunityLabel,
          awarded: invoice.feeHoursBased ? 0 : Number(invoice.recovered) || 0,
          recovered: invoice.feeHoursBased ? 0 : Number(invoice.recovered) || 0,
          hoursBased: Boolean(invoice.feeHoursBased),
          feeHoursBased: Boolean(invoice.feeHoursBased),
          dueDate: invoice.dueDate || null,
          awardedDate: invoice.awardedDate || null,
          appliedDate: invoice.appliedDate || null,
          cashReceivedDate: invoice.cashReceivedDate || null,
          feePercent: invoice.feeHoursBased ? null : invoice.feePercent,
          termsLabel: invoice.feeHoursBased
            ? null
            : "Net 15 from cash / Net 90 from award",
          memo: [lineMeta.memo, planNote].filter(Boolean).join(" · ") || null,
          quantity: lineMeta.quantity,
          unitAmount: lineMeta.unitAmount,
          amount: feeOwed,
          interestAccrued,
          paymentPlan: invoice.paymentPlan || null,
          note: lineMeta.note,
        },
      ]
    : [];
  const allLineItems = [...feeLineItems, ...retainerWithInterest];

  const dueBuckets = standalone
    ? {
        dueToday: totalDue,
        dueTodayWithoutInterest: Math.round(
          (totalDue - totalInterest) * 100
        ) / 100,
        dueLater: 0,
        dueLaterNote: "",
        hasFutureDue: false,
        showDueToday: false,
      }
    : resolveInvoiceDueBuckets({
        lineItems: allLineItems,
        taxTotal: taxed.taxTotal,
        interestAccrued: totalInterest,
        paidToDate: credits.paidToDate,
        asOf,
      });

  return {
    seller: {
      name: seller.name,
      legalName: seller.legalName,
      addressLines: seller.addressLines || [],
      taxNumber: seller.taxNumber || "",
    },
    billTo: {
      name: billToName,
      lines: billToLines,
      province: taxed.province,
      website: company.Domain || company.Website || company.domain || null,
    },
    meta: {
      invoiceNumber: invoice.invoiceNumber || "—",
      invoiceDate,
      // Statement is payable on the invoice date; Net 15/90 clocks live on each line.
      dueDate: invoiceDate,
      netTermsDays: invoice.netTermsDays || DEFAULT_NET_TERMS_DAYS,
      termsLabel: null,
      status: invoice.status,
      combined: false,
      lineCount: allLineItems.length,
      cashReceivedDate: invoice.cashReceivedDate || null,
      awardedDate: invoice.awardedDate || null,
      effectiveStart: invoice.effectiveStart || null,
    },
    lineItems: allLineItems,
    paymentPlans: invoice.paymentPlan ? [invoice.paymentPlan] : [],
    unreconciledPayments: interestPaymentCredits.filter((c) => c.unreconciled),
    subtotal: feeSubtotal,
    totalAwarded: invoice.feeHoursBased
      ? 0
      : Number(invoice.recovered) || 0,
    tax: taxed,
    taxLines: taxed.lines.map((line) => ({
      ...line,
      rateLabel: formatTaxPercent(line.rate),
    })),
    taxTotal: taxed.taxTotal,
    interestAccrued: totalInterest,
    totalDue,
    ...credits,
    dueToday: dueBuckets.dueToday,
    dueTodayWithoutInterest: dueBuckets.dueTodayWithoutInterest,
    dueLater: dueBuckets.dueLater,
    dueLaterNote: dueBuckets.dueLaterNote,
    hasFutureDue: dueBuckets.hasFutureDue,
    showDueToday: dueBuckets.showDueToday,
    paymentLines,
    paymentsLedgerSum,
    successFeePaymentsLedgerSum,
    payment: {
      link: invoice.paymentLink || "",
      linkWithInterest: invoice.paymentLink || "",
      linkWithoutInterest: "",
      expiresInDays: INVOICE_PAYMENT_LINK_EXPIRY_DAYS,
      expiryNote: INVOICE_PAYMENT_LINK_EXPIRY_NOTE,
      note: seller.paymentNote,
      financingLink: FINANCING_PARTNERS_URL,
    },
    interestBrief: INVOICE_INTEREST_BRIEF,
    footer: INVOICE_PAYMENT_TERMS,
  };
};

/**
 * One invoice / statement covering every open invoice in the set.
 * Line items = each opportunity fee; tax rolled up (shared province or per-line sum).
 */
export const buildCombinedInvoiceDocument = (
  invoices = [],
  {
    seller = DEFAULT_INVOICE_SELLER,
    companyFields = null,
    asOf = new Date(),
    paidToDate = null,
    payments = null,
    retainerLines = null,
    interestCredits = null,
  } = {}
) => {
  const rows = (invoices || []).filter(Boolean);
  const company = companyFields || rows[0]?.companyFields || {};
  const interestPaymentCredits =
    interestCredits != null
      ? interestCredits
      : payments != null
        ? paymentsAsInterestCredits(payments)
        : [];
  const retainerPreview =
    retainerLines != null
      ? normalizeRetainerLines(retainerLines, asOf)
      : normalizeRetainerLines(
          parseMissedRetainerMonthsField(company[MISSED_RETAINER_MONTHS_FIELD]),
          asOf
        );

  if (rows.length === 0) {
    if (!retainerPreview.length) return null;
    // Retainer-only statement (no billed success fees yet)
    return buildInvoiceDocument(
      {
        id: "retainer-only",
        companyName:
          company.Name || company.CompanyName || "—",
        companyId: null,
        companyFields: company,
        feeOwed: 0,
        feeHoursBased: true,
        recovered: 0,
        dueDate: null,
        interestAccrued: 0,
        tax: resolveInvoiceTax(0, company),
        upgradeName: "Retainer",
        opportunityName: "",
        status: "Open",
        netTermsDays: DEFAULT_NET_TERMS_DAYS,
      },
      {
        seller,
        companyFields: company,
        asOf,
        paidToDate,
        payments,
        retainerLines: retainerPreview,
        interestCredits: interestPaymentCredits,
      }
    );
  }

  if (rows.length === 1) {
    return buildInvoiceDocument(rows[0], {
      seller,
      companyFields,
      asOf,
      paidToDate,
      payments,
      retainerLines,
      interestCredits: interestPaymentCredits,
    });
  }

  const primary = rows[0];
  const companyNames = [
    ...new Set(rows.map((r) => r.companyName).filter(Boolean)),
  ];
  const sameCompany = companyNames.length <= 1;

  const lineItemsBase = rows.map((invoice) => {
    const feeOwed = Number(invoice.feeOwed) || 0;
    const upgradeLabel =
      String(invoice.upgradeName || "").trim() || "Opportunity";
    const opportunityLabel = String(invoice.opportunityName || "").trim();
    const lineMeta = resolveInvoiceLineMeta(invoice, { sameCompany });
    const planNote = invoice.paymentPlan?.months
      ? `${invoice.paymentPlan.months}-mo plan`
      : null;
    return {
      id: invoice.id,
      description: lineMeta.description,
      upgradeName: upgradeLabel,
      opportunityName: opportunityLabel,
      awarded: invoice.feeHoursBased ? 0 : Number(invoice.recovered) || 0,
      recovered: invoice.feeHoursBased ? 0 : Number(invoice.recovered) || 0,
      hoursBased: Boolean(invoice.feeHoursBased),
      feeHoursBased: Boolean(invoice.feeHoursBased),
      dueDate: invoice.dueDate || null,
      awardedDate: invoice.awardedDate || null,
      appliedDate: invoice.appliedDate || null,
      cashReceivedDate: invoice.cashReceivedDate || null,
      feePercent: invoice.feeHoursBased ? null : invoice.feePercent,
      termsLabel: invoice.feeHoursBased
        ? null
        : "Net 15 from cash / Net 90 from award",
      memo: [lineMeta.memo, planNote].filter(Boolean).join(" · ") || null,
      quantity: lineMeta.quantity,
      unitAmount: lineMeta.unitAmount,
      amount: feeOwed,
      paymentPlan: invoice.paymentPlan || null,
      note: lineMeta.note,
    };
  });

  const retainer =
    retainerLines != null
      ? normalizeRetainerLines(retainerLines, asOf)
      : sameCompany
        ? normalizeRetainerLines(
            parseMissedRetainerMonthsField(
              company[MISSED_RETAINER_MONTHS_FIELD]
            ),
            asOf
          )
        : [];

  const interestObligations = [
    ...lineItemsBase.map((item) => ({
      key: String(item.id),
      principal: item.amount,
      dueDate: item.dueDate,
      paymentPlan: item.paymentPlan,
    })),
    ...retainer
      .filter((r) => !r.excludedFromTotal)
      .map((r) => ({
        key: String(r.id),
        principal: r.listedAmount || r.amount,
        dueDate: r.dueDate,
        paymentPlan: null,
      })),
  ];
  const interestByKey = calculateLineInterestsWithPayments({
    lines: interestObligations,
    interestCredits: interestPaymentCredits,
    asOf,
  });

  const lineItems = lineItemsBase.map((item) => ({
    ...item,
    interestAccrued: interestByKey[String(item.id)] || 0,
  }));
  const retainerWithInterest = retainer.map((r) => ({
    ...r,
    interestAccrued: r.excludedFromTotal
      ? 0
      : interestByKey[String(r.id)] || 0,
  }));
  const allLineItems = [...lineItems, ...retainerWithInterest];

  const feeSubtotal = lineItems.reduce((s, item) => s + (item.amount || 0), 0);
  const retainerSubtotal = retainerWithInterest.reduce(
    (s, r) => s + (r.amount || 0),
    0
  );
  const subtotal = feeSubtotal + retainerSubtotal;
  const totalAwarded = lineItems.reduce((s, item) => {
    if (item.hoursBased || item.feeHoursBased) return s;
    return s + (Number(item.awarded) || 0);
  }, 0);
  const feeInterest = lineItems.reduce(
    (s, item) => s + (Number(item.interestAccrued) || 0),
    0
  );
  const retainerInterest = retainerWithInterest.reduce(
    (s, r) => s + (Number(r.interestAccrued) || 0),
    0
  );
  const totalInterest =
    Math.round((feeInterest + retainerInterest) * 100) / 100;

  let tax;
  if (sameCompany) {
    tax = resolveInvoiceTax(subtotal, company);
  } else {
    // Different companies / provinces — sum each invoice's tax lines by label
    const byLabel = {};
    rows.forEach((invoice) => {
      const invTax =
        invoice.tax ||
        resolveInvoiceTax(invoice.feeOwed, invoice.companyFields || {});
      (invTax.lines || []).forEach((line) => {
        const key = `${line.label}|${line.rate}`;
        if (!byLabel[key]) {
          byLabel[key] = { label: line.label, rate: line.rate, amount: 0 };
        }
        byLabel[key].amount += line.amount || 0;
      });
    });
    const lines = Object.values(byLabel).map((line) => ({
      ...line,
      amount: Math.round(line.amount * 100) / 100,
    }));
    const taxTotal = lines.reduce((s, l) => s + l.amount, 0);
    tax = {
      province: null,
      type: "Mixed",
      lines,
      taxTotal,
      totalWithTax: subtotal + taxTotal,
      usedOverride: false,
    };
  }

  const invoiceDate = (parseDateOnly(asOf) || new Date())
    .toISOString()
    .slice(0, 10);
  const billToName =
    company.Name ||
    company.CompanyName ||
    (sameCompany ? primary.companyName : "Multiple companies") ||
    "—";
  const billToLines = sameCompany
    ? buildBillToLines(company, billToName)
    : [billToName, `${rows.length} open success-fee line items`];

  const invoiceNumber =
    sameCompany && primary.companyId
      ? `AR-${String(primary.companyId).slice(-6)}`
      : `AR-${invoiceDate.replace(/-/g, "")}`;

  const totalDue =
    Math.round((tax.totalWithTax + totalInterest) * 100) / 100;
  const paymentLines = normalizeInvoicePaymentLines(payments);
  const paymentsLedgerSum = paymentLines.length
    ? sumPaymentLinesCAD(paymentLines)
    : null;
  const successFeePaymentsLedgerSum =
    payments != null ? sumSuccessFeePaymentLinesCAD(paymentLines) : null;
  const effectivePaid =
    successFeePaymentsLedgerSum != null
      ? successFeePaymentsLedgerSum
      : sameCompany &&
          company?.SuccessFeePaidToDate != null &&
          company?.SuccessFeePaidToDate !== ""
        ? company.SuccessFeePaidToDate
        : paidToDate != null && paidToDate !== ""
          ? paidToDate
          : sameCompany &&
              company?.PaidToDate != null &&
              company?.PaidToDate !== ""
            ? company.PaidToDate
            : paymentsLedgerSum;
  const credits = resolveInvoicePaidCredits(
    totalDue,
    effectivePaid,
    sameCompany ? company : {}
  );

  const dueBuckets = resolveInvoiceDueBuckets({
    lineItems: allLineItems,
    taxTotal: tax.taxTotal,
    interestAccrued: totalInterest,
    paidToDate: credits.paidToDate,
    asOf,
  });

  return {
    seller: {
      name: seller.name,
      legalName: seller.legalName,
      addressLines: seller.addressLines || [],
      taxNumber: seller.taxNumber || "",
    },
    billTo: {
      name: billToName,
      lines: billToLines,
      province: tax.province,
      website: company.Domain || company.Website || company.domain || null,
    },
    meta: {
      invoiceNumber,
      invoiceDate,
      // Statement is payable on the invoice date; Net 15/90 clocks live on each line.
      dueDate: invoiceDate,
      netTermsDays: primary.netTermsDays || DEFAULT_NET_TERMS_DAYS,
      termsLabel: null,
      status: "Open",
      combined: true,
      lineCount: allLineItems.length,
    },
    lineItems: allLineItems,
    subtotal,
    totalAwarded,
    tax,
    taxLines: tax.lines.map((line) => ({
      ...line,
      rateLabel: formatTaxPercent(line.rate),
    })),
    taxTotal: tax.taxTotal,
    interestAccrued: totalInterest,
    totalDue,
    ...credits,
    dueToday: dueBuckets.dueToday,
    dueTodayWithoutInterest: dueBuckets.dueTodayWithoutInterest,
    dueLater: dueBuckets.dueLater,
    dueLaterNote: dueBuckets.dueLaterNote,
    hasFutureDue: dueBuckets.hasFutureDue,
    showDueToday: dueBuckets.showDueToday,
    paymentLines,
    paymentsLedgerSum,
    successFeePaymentsLedgerSum,
    unreconciledPayments: interestPaymentCredits.filter((c) => c.unreconciled),
    paymentPlans: rows.map((r) => r.paymentPlan).filter(Boolean),
    payment: {
      link: primary.paymentLink || "",
      linkWithInterest: primary.paymentLink || "",
      linkWithoutInterest: "",
      expiresInDays: INVOICE_PAYMENT_LINK_EXPIRY_DAYS,
      expiryNote: INVOICE_PAYMENT_LINK_EXPIRY_NOTE,
      note: seller.paymentNote,
      financingLink: FINANCING_PARTNERS_URL,
    },
    interestBrief: INVOICE_INTEREST_BRIEF,
    footer: INVOICE_PAYMENT_TERMS,
  };
};

/**
 * Statement A/R for one company — same balanceDue as monthly invoices / panel.
 * @param {object} opts
 * @param {Array} opts.opportunities — Airtable-shaped opp records for this company
 * @param {{ id?: string, fields?: object }|null} opts.company
 * @param {number|null} [opts.paidToDate]
 * @param {Array|null} [opts.payments]
 * @param {Date|string} [opts.asOf]
 */
export const computeCompanyStatementBalance = ({
  opportunities = [],
  company = null,
  paidToDate = null,
  payments = null,
  asOf = new Date(),
} = {}) => {
  const companyFields = company?.fields || {};
  const companiesOpt = company?.id
    ? [{ id: company.id, fields: companyFields }]
    : companyFields && Object.keys(companyFields).length
      ? [{ id: "company", fields: companyFields }]
      : null;

  const invoices = (opportunities || [])
    .map((record) =>
      buildInvoiceRecord(
        { id: record.id, fields: record.fields },
        { companies: companiesOpt, asOf }
      )
    )
    .filter((inv) => inv.open && inv.feeOwed > 0);

  const retainerStored = retainerRangeFromStored(
    companyFields[MISSED_RETAINER_MONTHS_FIELD]
  );
  const retainerLines =
    Number(retainerStored.monthlyAmount) > 0 &&
    retainerStored.startDate &&
    retainerStored.endDate
      ? {
          monthlyAmount: Number(retainerStored.monthlyAmount) || 0,
          startDate: retainerStored.startDate,
          endDate: retainerStored.endDate,
          excludedIds: retainerStored.excludedIds || [],
        }
      : null;

  if (!invoices.length && !retainerLines) {
    return {
      balanceDue: 0,
      totalDue: 0,
      invoiceCount: 0,
      doc: null,
    };
  }

  const paid =
    paidToDate != null && paidToDate !== ""
      ? Number(paidToDate) || 0
      : companyFields.PaidToDate != null && companyFields.PaidToDate !== ""
        ? Number(companyFields.PaidToDate) || 0
        : null;

  const doc = buildCombinedInvoiceDocument(invoices, {
    companyFields,
    ...(paid != null ? { paidToDate: paid } : {}),
    ...(payments != null ? { payments } : {}),
    retainerLines,
    asOf,
  });

  if (!doc) {
    return {
      balanceDue: 0,
      totalDue: 0,
      invoiceCount: invoices.length,
      doc: null,
    };
  }

  const balanceDue =
    doc.balanceDue != null
      ? Math.max(0, Number(doc.balanceDue) || 0)
      : Math.max(0, Number(doc.totalDue) || 0);

  return {
    balanceDue,
    totalDue: Math.max(0, Number(doc.totalDue) || 0),
    invoiceCount: invoices.length,
    doc,
  };
};

export const buildOpenInvoices = (opportunities = [], options = {}) =>
  (opportunities || [])
    .map((opp) => buildInvoiceRecord(opp, options))
    .filter((row) => row.open)
    .sort((a, b) => (b.daysOverdue || 0) - (a.daysOverdue || 0));

export const summarizeAccountsReceivable = (
  opportunities = [],
  options = {}
) => {
  const invoices = (opportunities || []).map((opp) =>
    buildInvoiceRecord(opp, options)
  );
  const open = invoices.filter((row) => row.open);
  const paid = invoices.filter((row) => row.paid);

  const byStage = AR_STAGES.reduce((acc, stage) => {
    acc[stage.id] = {
      ...stage,
      count: 0,
      feeOwed: 0,
      teamPayout: 0,
      invoices: [],
    };
    return acc;
  }, {});

  open.forEach((row) => {
    const bucket = byStage[row.stage.id];
    if (!bucket) return;
    bucket.count += 1;
    bucket.feeOwed += row.feeOwed;
    bucket.teamPayout += row.teamPayout;
    bucket.invoices.push(row);
  });

  const byAssignee = {};
  open.forEach((row) => {
    const key = row.assignee || "Unassigned";
    if (!byAssignee[key]) {
      byAssignee[key] = { assignee: key, count: 0, feeOwed: 0, teamPayout: 0 };
    }
    byAssignee[key].count += 1;
    byAssignee[key].feeOwed += row.feeOwed;
    byAssignee[key].teamPayout += row.teamPayout;
  });

  return {
    open,
    paid,
    invoices,
    byStage,
    byAssignee: Object.values(byAssignee).sort(
      (a, b) => b.teamPayout - a.teamPayout
    ),
    totals: {
      openCount: open.length,
      feeOwed: open.reduce((s, r) => s + r.feeOwed, 0),
      taxTotal: open.reduce((s, r) => s + (r.taxTotal || 0), 0),
      totalDue: open.reduce((s, r) => s + (r.totalDue || r.feeOwed), 0),
      teamPayout: open.reduce((s, r) => s + r.teamPayout, 0),
      recovered: open.reduce((s, r) => s + r.recovered, 0),
      paidCount: paid.length,
      paidFees: paid.reduce((s, r) => s + r.feeOwed, 0),
    },
  };
};

/**
 * Company-balance aging when opportunity invoices aren't loaded.
 * Uses days since LastPaidDate (or ClosedRenewalDate) as overdue proxy.
 */
export const getCompanyBalanceDaysOverdue = (fields, asOf = new Date()) => {
  if (!fields) return null;
  const anchor =
    parseDateOnly(fields.LastPaidDate) ||
    parseDateOnly(fields.ClosedRenewalDate);
  if (!anchor) return null;
  const today = parseDateOnly(asOf) || new Date();
  // Net-30 after last payment / renewal start for outstanding balance age
  const due = new Date(anchor);
  due.setDate(due.getDate() + DEFAULT_NET_TERMS_DAYS);
  return daysBetween(due, today);
};

export const getCompanyBalanceArStage = (fields, balance = 0, asOf = new Date()) => {
  if (!(Number(balance) > 0)) {
    return AR_STAGES.find((s) => s.id === "current");
  }
  const days = getCompanyBalanceDaysOverdue(fields, asOf);
  return getArStage(days == null ? 1 : days);
};

export const summarizeCompanyBalanceAging = (
  companies = [],
  getBalance = () => 0,
  asOf = new Date()
) => {
  const byStage = AR_STAGES.reduce((acc, stage) => {
    acc[stage.id] = { ...stage, count: 0, balance: 0 };
    return acc;
  }, {});

  (companies || []).forEach((company) => {
    const fields = company.fields || company;
    const balance = Number(getBalance(company)) || 0;
    if (!(balance > 0)) return;
    const stage = getCompanyBalanceArStage(fields, balance, asOf);
    const bucket = byStage[stage.id];
    if (!bucket) return;
    bucket.count += 1;
    bucket.balance += balance;
  });

  return byStage;
};

const formatDateLabel = (isoOrDate) => {
  if (!isoOrDate) return "[date]";
  const d = parseDateOnly(isoOrDate);
  if (!d) return "[date]";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const addDaysIso = (base, days) => {
  const d = parseDateOnly(base) || new Date();
  d.setDate(d.getDate() + days);
  return formatDateLabel(d);
};

/**
 * Suggested outreach copy for Stages 1–5 (playbook + email templates).
 */
export const buildArReminderCopy = (
  invoice,
  stageId = null,
  { includePaymentLink = true } = {}
) => {
  const stage = stageId
    ? AR_STAGES.find((s) => s.id === stageId) || invoice.stage
    : invoice.stage;
  const name = "[Name]";
  const amount = formatArCurrency(invoice.feeOwed);
  const recovered = formatArCurrency(invoice.recovered);
  const invoiceNo = invoice.invoiceNumber || "[X]";
  const link = includePaymentLink ? invoice.paymentLink || "[link]" : "";
  const days = Math.max(0, invoice.daysOverdue || 0);
  const net = invoice.netTermsDays || DEFAULT_NET_TERMS_DAYS;
  const confirmBy = addDaysIso(new Date(), 7);

  switch (stage?.id) {
    case "gentle":
      return {
        stageId: "gentle",
        subject: `Quick heads up — invoice #${invoiceNo}`,
        body: includePaymentLink
          ? `Hi ${name} — quick heads up that invoice #${invoiceNo} for ${amount} (against the ${recovered} we secured for you) is now a few days past due. Probably just slipped through — here's the link to settle it: ${link}.\n\nLet me know if anything's unclear.`
          : `Hi ${name} — quick heads up that invoice #${invoiceNo} for ${amount} (against the ${recovered} we secured for you) is now a few days past due. Probably just slipped through.\n\nLet me know if anything's unclear.`,
      };
    case "firm":
      return {
        stageId: "firm",
        subject: `Following up — invoice #${invoiceNo} (${days} days past due)`,
        body: includePaymentLink
          ? `Hi ${name} — following up on invoice #${invoiceNo}, now ${days} days past our net-${net} terms. The ${recovered} has landed in your account, so I'd like to close this out on our end. Can you confirm payment by ${confirmBy}? Link here: ${link}.`
          : `Hi ${name} — following up on invoice #${invoiceNo}, now ${days} days past our net-${net} terms. The ${recovered} has landed in your account, so I'd like to close this out on our end. Can you confirm payment by ${confirmBy}?`,
      };
    case "escalation":
      return {
        stageId: "escalation",
        subject: `Let's sort out invoice #${invoiceNo} on a quick call`,
        body: `${name} — invoice #${invoiceNo} is now significantly overdue and I'd like to sort it out directly rather than over email. Are you free for 10 minutes this week?\n\nIf cash timing is the issue, we can set up a short payment plan — just tell me what works.`,
      };
    case "final":
      return {
        stageId: "final",
        subject: `Formal notice — invoice #${invoiceNo}`,
        body: `${name} — this is a formal notice on invoice #${invoiceNo}, ${amount}, now ${days} days overdue against signed terms. To avoid interest accruing per our agreement / pausing work on your next filing, we need payment or a confirmed plan by ${confirmBy}. Happy to jump on a call today.`,
      };
    case "collections":
      return {
        stageId: "collections",
        subject: `Final escalation — invoice #${invoiceNo}`,
        body: `${name} — invoice #${invoiceNo} (${amount}, ${days} days overdue) is being prepared for collections. If we do not receive payment or a written payment plan by ${confirmBy}, we will escalate to a collections agency and may pursue legal remedies under our agreement.\n\nReply to this email or call us today if you want to resolve this before that step.`,
      };
    default:
      return {
        stageId: "current",
        subject: `Invoice #${invoiceNo} for ${amount}`,
        body: includePaymentLink
          ? `Hi ${name} — invoice #${invoiceNo} for ${amount} (against the ${recovered} we secured for you) is due ${formatDateLabel(invoice.dueDate)}. Settle here: ${link}.`
          : `Hi ${name} — invoice #${invoiceNo} for ${amount} (against the ${recovered} we secured for you) is due ${formatDateLabel(invoice.dueDate)}.`,
      };
  }
};
